// Tasks — the standing list and the completed record (docs/SPEC-001.md §12,
// §14; INV-1, INV-2, INV-9; owner ruling R-15).
//
// THE SHAPE OF THIS PAGE, and why it changed on 2026-08-15. The owner, in his
// own words:
//
//   "The task list technically should be the same for each day so it doesn't
//    change when you change a day … every time it submits it should go into a
//    lower table called Completed items. So under daily, weekly, monthly each
//    will also have a completed table for that day. So really the day picker
//    should be for the bottom table … anytime a day or week or month goes by
//    and an item wasn't done within its time frame it should be logged as
//    incomplete to the lower table."
//   "That's how we will also track who isn't doing their job."
//
// So there are TWO tables and they answer two different questions:
//
//   TOP    — the STANDING list: what is supposed to happen, every period. It
//            comes from `task_definition` (via `state.value.standing`), so it
//            is identical on every day and the day picker does not touch it.
//            Only OPEN items are here; submitting one moves it down.
//   BOTTOM — what actually happened in ONE period. The day picker moves this
//            half and only this half.
//
// The old page had only the bottom half and pointed the day picker at it, so
// changing the day changed the whole list. That is what the owner said was
// wrong.
//
// INCOMPLETE IS DERIVED, NEVER STORED — the same rule that keeps OVERDUE out of
// the status vocabulary (finding F-29, migration 007's note): `period_end` in
// the past AND a status that is not terminal. A nightly job that writes
// INCOMPLETE onto rows is one more thing that can silently stop running in a
// system with no IT department, and a stored copy of a computed fact drifts
// from its source. Owner ruling, 2026-08-15: a late completion FLIPS to Done
// and keeps the date it was actually done, so the lateness stays visible in the
// timestamp rather than in a frozen label.
//
// Writes go straight to the API — INV-1 budgets NAVIGATION at zero network
// calls, not user actions. The standing list itself is loaded once at
// bootstrap (client/src/lib/bootstrap.ts), so switching cadence tabs costs
// nothing.

import { AssistantsPanel } from '../components/AssistantsPanel';
import { TaskStatsView, TaskAlertsView, useAlertsBadge } from '../components/TaskStatsPanel';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { reportLabelForTask, reportShortFromLabel } from '../lib/reportTaskLink';
import { MEEVO_REPORTS } from '../lib/meevoReports';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import {
  state, upsertEntity, putEntityTable, useResource, refetchResource, peekResource,
  persistResource, primeResource,
} from '../lib/store';
import { useWrite } from '../components/MoneyForms';
import { session } from '../lib/session';
import { authedGet, authedWrite, ApiError } from '../lib/apiClient';
import { formatDate, formatIsoWeek } from '../lib/date';
import { FaultState, EmptyState } from '../components/FaultState';
import { ClinicTabs, visibleClinics, activeClinicFrom, MANAGEMENT_TAB_ID, canSeeManagementTab } from '../components/ClinicTabs';
import { ManagementPlaceholder, SHOW_EMPTY_MANAGEMENT_TABS } from '../components/ManagementPlaceholder';
import { clinicToday, clinicNow, formatReportLanded, CLINIC_TIME_ZONE } from '../lib/clinicDay';
import { uploadTaskReport } from '../lib/reportFile';
import { uploadTaskFile } from '../lib/taskFile';
import { snapshot } from '../lib/bootstrap';
import { TaskFileLink, InstructionsText } from '../components/TaskFileLink';
import { driveIdOf, type KnownTaskFile, type TaskFileKind } from '../lib/taskFileOpen';
import { NoteMark, NotePanel } from '../components/NotePanel';

// The ten statuses (SPEC-001 §5, §12). Nine are stored (db/migrations/007_tasks.sql);
// OVERDUE is the tenth and is DERIVED, never stored (see that migration's note
// on finding F-29) — computed here at render time from period_end vs. now.
const STORED_STATUSES = [
  'NOT_STARTED', 'IN_PROGRESS', 'BLOCKED', 'SKIPPED',
  'DONE', 'VERIFIED', 'SENT_BACK', 'CLOSED', 'N_A',
] as const;
type StoredStatus = (typeof STORED_STATUSES)[number];
type DisplayStatus = StoredStatus | 'OVERDUE' | 'INCOMPLETE';

/** The statuses a person may choose from the dropdown. VERIFIED and SENT_BACK
 * are absent because each has its own endpoint with rules this generic control
 * must not route around — the server refuses them here by name. */
const SETTABLE_STATUSES: readonly StoredStatus[] = [
  'NOT_STARTED', 'IN_PROGRESS', 'BLOCKED', 'SKIPPED', 'DONE', 'CLOSED', 'N_A',
];

/** The states that mean "this task is finished with" — a Submit button on one
 * of these is a control that can only do harm, and a row in one of them belongs
 * in the completed table rather than the standing list. */
export const TERMINAL_STATUSES: readonly StoredStatus[] = [
  'DONE', 'VERIFIED', 'SKIPPED', 'N_A', 'CLOSED',
];

export const STATUS_LABEL: Record<DisplayStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  SKIPPED: 'Skipped',
  DONE: 'Done',
  VERIFIED: 'Verified',
  SENT_BACK: 'Sent back',
  CLOSED: 'Closed',
  N_A: 'N/A',
  OVERDUE: 'Overdue',
  INCOMPLETE: 'Incomplete',
};

/** The one-line detail under a status. Colour comes from the `.good` / `.bad`
 * classes, never an inline style, so there is no conflict with INV-8's
 * design-law suite. `null` means the status needs no second line. */
function stateDetail(status: DisplayStatus, row: TaskRow): { cls: 'good' | 'bad'; text: string } | null {
  switch (status) {
    case 'DONE':
    case 'VERIFIED':
      return { cls: 'good', text: doneDetail(row) };
    case 'SENT_BACK':
      return { cls: 'bad', text: row.sent_back_reason ? `Sent back: ${row.sent_back_reason}` : 'Sent back' };
    case 'OVERDUE':
      return { cls: 'bad', text: 'Overdue' };
    case 'INCOMPLETE':
      return { cls: 'bad', text: 'Not done in its time frame' };
    case 'BLOCKED':
      return { cls: 'bad', text: 'Blocked' };
    case 'SKIPPED':
      return { cls: 'good', text: 'Skipped' };
    default:
      return null;
  }
}

/**
 * "Done by <who> on <MM/DD/YYYY> at <2:05 PM>" — the accountability record the
 * owner asked for: "that's how we will also track who isn't doing their job."
 *
 * Date AND time, under owner ruling R-15 (2026-08-15, verbatim: "yes it gets
 * date and timestamped when done"). The date half is still MM/DD/YYYY and no
 * timezone is ever shown.
 */
export function doneDetail(row: TaskRow): string {
  const who = row.completed_by ? `Done by ${row.completed_by}` : 'Done';
  if (!row.completed_at) return who;
  const at = new Date(row.completed_at);
  if (Number.isNaN(at.getTime())) return who;
  return `${who} on ${formatDate(clinicNow(at))} at ${formatClock(at)}`;
}

/** "2:05 PM" — a 12-hour clock, no seconds, no timezone (R-15), on the
 * clinic's Eastern clock, never the viewer's. */
export function formatClock(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(at);
  const pick = (t: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${pick('hour')}:${pick('minute')} ${pick('dayPeriod').toUpperCase()}`;
}

export interface TaskRow {
  // NULL on a standing row nobody has touched this period: no task_status row
  // exists yet, and the Submit path creates it. This nullability IS the fix for
  // the defect that shipped — the old INNER-join read returned nothing at all
  // for an untouched period, so the list vanished.
  id: number | null;
  task_definition_id: number;
  definition_name: string;
  cadence: 'WEEKLY' | 'DAILY' | 'MONTHLY' | 'MANAGER';
  due_rule: string;
  duration?: string | null;
  automated: boolean;
  location_code: string;
  task_period_id: number | null;
  period_label: string;
  period_start: string;
  period_end: string;
  status: StoredStatus;
  completed_by: string | null;
  completed_at: string | null;
  verified_by: string | null;
  verified_at: string | null;
  sent_back_reason: string | null;
  report_url?: string | null;
  notes?: string | null;
  note_count?: number | null;
  updated_at: string | null;
  period_closed?: boolean;
  // The task PROFILE (migration 033). These belong to the DEFINITION, not to
  // this clinic's period — the instructions for "count the drawer" are the same
  // at all four clinics and on every day — so every standing row for one
  // definition carries the same copy of them.
  category?: string | null;
  instructions?: string | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
  filed_report?: boolean | null;
  report_landed_at?: string | null;
  report_label?: string | null;
  report_slot?: string | null;
  report_driven?: boolean | null;
  files?: { id: number; url: string; name: string | null }[];
  instructions_updated_at?: string | null;
  instructions_updated_by?: string | null;
}

/** OVERDUE is derived, never stored (F-29): a task still open once its period
 * has ended. A terminal status is never overdue, however late the clock runs. */
export function displayStatus(row: TaskRow, now: Date): DisplayStatus {
  if (TERMINAL_STATUSES.includes(row.status)) return row.status;
  // Compared as DATES, not instants. `period_end` is a `date` column with no
  // time and no zone; treating it as a moment marked today's daily task overdue
  // from midnight UTC — mid-evening in Florida (F-62's family).
  // "Today" is the CLINIC's day (America/New_York), never the browser's: read
  // from seven hours ahead, a browser-local day marked every open task OVERDUE
  // against a period the clinics were still working in (2026-08-17).
  const end = asDateString(row.period_end);
  const today = clinicToday(now);
  if (end && end < today) return 'OVERDUE';
  return row.status;
}

/**
 * The completed table's own reading of a row (owner, 2026-08-15): "anytime a
 * day or week or month goes by and an item wasn't done within its time frame it
 * should be logged as incomplete."
 *
 * Same derivation as OVERDUE, different word for it — OVERDUE is what an open
 * task is called while you can still do something about it; INCOMPLETE is what
 * the record calls it once the period is over. Both are computed from the same
 * two facts, so the two tables can never disagree about one row.
 */
export function recordStatus(row: TaskRow, now: Date): DisplayStatus {
  const derived = displayStatus(row, now);
  return derived === 'OVERDUE' ? 'INCOMPLETE' : derived;
}

/**
 * Does this row belong in the completed table?
 *
 * Yes if something HAPPENED to it (a terminal status), or if its period ended
 * with it still open (INCOMPLETE — the owner's "wasn't done within its time
 * frame"). No if it is simply still open in a period that is still running:
 * that is the standing list's job, and printing it here reprinted the entire
 * top table underneath itself, thirteen tasks twice, every lower line reading
 * "Not started — — —".
 *
 * A `task_status` row exists for every definition from the moment a period is
 * seeded, so "has a row" was never the same question as "has been recorded".
 */
export function isRecorded(row: TaskRow, now: Date): boolean {
  return TERMINAL_STATUSES.includes(row.status) || recordStatus(row, now) === 'INCOMPLETE';
}

/** A date bound reduced to YYYY-MM-DD, whether it arrived as a plain date or as
 * a driver-rendered timestamp. Local parts, so "today" is the day it is at the
 * clinic. */
function asDateString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  // A date column rendered as midnight UTC still names that one day.
  const midnight = /^(\d{4}-\d{2}-\d{2})T0+(?::0+)+(?:\.0+)?Z$/.exec(value);
  if (midnight) return midnight[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return clinicToday(d);
}

// R-10: the bare string "Week 33" is a build failure. Every rendered period
// label goes through this.
const ISO_WEEK = /^\d{4}-W\d{2}$/;
export function renderPeriodLabel(row: TaskRow): string {
  const start = new Date(row.period_start);
  const end = new Date(row.period_end);
  if (ISO_WEEK.test(row.period_label) && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    return formatIsoWeek(row.period_label, row.period_start, row.period_end);
  }
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return row.period_label;
  }
  // A DAILY period is one day, so start and end are the same date and the stored
  // label already contains it. Appending the range produced
  // "Day 2026-08-15 (Aug 15) (08/15/2026–08/15/2026)" — the same date three
  // times over, which is exactly the kind of text the owner asked to be rid of
  // (2026-08-15). One day, rendered once, in the app's own date format.
  const from = formatDate(row.period_start);
  const to = formatDate(row.period_end);
  if (from === to) return from;
  return `${row.period_label} (${from}–${to})`;
}

function getTaskTable(): Record<string, TaskRow> {
  return (state.value.task ?? {}) as Record<string, TaskRow>;
}

/** The standing list, loaded once at bootstrap and keyed `<definition>:<clinic>`. */
function getStandingTable(): Record<string, TaskRow> {
  return (state.value.standing ?? {}) as Record<string, TaskRow>;
}

/** Every period_label present in the store, most recent period_start first. */
export function periodsFrom(rows: TaskRow[]): { label: string; start: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows) {
    if (!seen.has(r.period_label) || r.period_start < seen.get(r.period_label)!) {
      seen.set(r.period_label, r.period_start);
    }
  }
  return [...seen.entries()]
    .map(([label, start]) => ({ label, start }))
    .sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : 0));
}

export interface TaskPeriodSummary {
  period_label: string;
  period_start: string;
  period_end: string;
  closed: boolean;
}

/** The DAILY period label covering `day` (YYYY-MM-DD): the one whose start and
 * end are both that day. `null` when the day is outside what the API returned. */
export function dailyLabelFor(periods: TaskPeriodSummary[], day: string): string | null {
  for (const p of periods) {
    if (asDateString(p.period_start) === day && asDateString(p.period_end) === day) {
      return p.period_label;
    }
  }
  return null;
}

/** A plain YYYY-MM-DD day as MM/DD/YYYY, built from its own parts — NOT
 * `new Date(day)`, which parses a bare date as UTC and renders the day BEFORE
 * it west of Greenwich (F-62). */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return day;
  return formatDate(new Date(y, m - 1, d));
}

/** Today as YYYY-MM-DD at the CLINICS — the one clock this app judges a day
 * by, wherever the person reading it happens to be (see lib/clinicDay.ts). */
export function todayString(now: Date): string {
  return clinicToday(now);
}

/** May this viewer add, rename, re-time or remove rows on this cadence?
 * Owner and director: all four lists. Manager: Manager Tasks only (INV-3). */
export function canEditStandingList(cadence?: string | null): boolean {
  const role = session.value.me?.role;
  if (role === 'OWNER' || role === 'DIRECTOR') return true;
  return role === 'MANAGER' && cadence === 'MANAGER';
}

// --- writes ---------------------------------------------------------------

/**
 * Set one task's status.
 *
 * Two paths, and which one runs depends on whether a `task_status` row exists
 * yet. A standing row nobody has touched this period has `id === null`, and
 * there is nothing to POST a status to — the row has to be created first. The
 * `/task-definitions/:id/submit` endpoint does both in one statement, so the
 * client never has to create-then-update and never has a window where the row
 * exists with no event.
 *
 * The response carries only the NARROW task_status shape, never the joined
 * definition_name / cadence / period_label this page renders, so it is MERGED
 * over the row already in the store rather than replacing it — replacing it
 * would blank the task's name the moment someone changed its status.
 */
export async function setStatus(row: TaskRow, status: StoredStatus): Promise<void> {
  const res = row.id === null
    ? await authedWrite<{ taskStatus: Partial<TaskRow> }>(
      'POST',
      `/api/v1/task-definitions/${row.task_definition_id}/submit`,
      { locationCode: row.location_code, status },
    )
    : await authedWrite<{ taskStatus: Partial<TaskRow> }>(
      'POST',
      `/api/v1/task-status/${row.id}/status`,
      { status },
    );
  const merged = { ...row, ...res.taskStatus } as TaskRow;
  // Both tables, always. The standing row keeps its place in the top list (so
  // the status control still works) and the completed table gets the row it
  // needs to show what happened — otherwise a task would be submitted and
  // simply vanish until the next reload.
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null && merged.id !== undefined) {
    await upsertEntity('task', merged.id, merged);
  }
  if (status === 'DONE') {
    const stamp = doneDetail(merged);
    const extra = typeof merged.notes === 'string' ? merged.notes.trim() : '';
    const body = extra ? `${stamp}. ${extra}` : `${stamp}.`;
    try {
      await authedWrite('POST', `/api/v1/task-definitions/${merged.task_definition_id}/notes`, { body });
    } catch {
      // The mark-done already landed. A failed stamp must not undo it.
    }
  }
}

/** Put a completed task back to open. Notes and files stay. */
export async function reopenTask(row: TaskRow): Promise<void> {
  if (row.id === null) return;
  const res = await authedWrite<{ taskStatus: Partial<TaskRow> }>(
    'POST',
    `/api/v1/task-status/${row.id}/reopen`,
    {},
  );
  const merged = { ...row, ...res.taskStatus } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null && merged.id !== undefined) {
    await upsertEntity('task', merged.id, merged);
  }
}

export function canUndoCompleted(row: TaskRow): boolean {
  // Reopen is refused for an assistant by the server, so the control is not drawn.
  if (session.value.me?.role === 'ASSISTANT') return false;
  return row.id !== null && !row.period_closed && TERMINAL_STATUSES.includes(row.status);
}

/** The store key for a standing row: one definition produces one row per
 * clinic, so the definition id alone would collide across four clinics. */
export function standingKey(row: { task_definition_id: number; location_code: string }): string {
  return `${row.task_definition_id}:${row.location_code}`;
}

/** One clinic's board: that clinic only, and each task name once.
 * Each clinic already has its own copy of a task in the database; listing
 * every copy on every clinic is what made the same line appear four times. */
export function forClinic(rows: TaskRow[], clinic: string): TaskRow[] {
  const mine = rows.filter((r) => r.location_code === clinic);
  const byName = new Map<string, TaskRow>();
  for (const r of mine) {
    const key = (r.definition_name || '').trim().toLowerCase();
    const prev = byName.get(key);
    if (!prev) {
      byName.set(key, r);
      continue;
    }
    const prevTouched = prev.id != null || (prev.status && prev.status !== 'NOT_STARTED');
    const nextTouched = r.id != null || (r.status && r.status !== 'NOT_STARTED');
    if (nextTouched && !prevTouched) byName.set(key, r);
    else if (prevTouched === nextTouched && r.task_definition_id < prev.task_definition_id) {
      byName.set(key, r);
    }
  }
  return [...byName.values()];
}

/** Save a task's note. An untouched standing row has no status id yet — this
 * opens one as NOT_STARTED first, then writes the note. Nobody has to Submit
 * just to leave a note. */
export async function saveNote(row: TaskRow, notes: string): Promise<void> {
  let current = row;
  if (current.id === null) {
    const opened = await authedWrite<{ taskStatus: Partial<TaskRow> }>(
      'POST',
      `/api/v1/task-definitions/${current.task_definition_id}/submit`,
      { locationCode: current.location_code, status: 'NOT_STARTED' },
    );
    current = { ...current, ...opened.taskStatus } as TaskRow;
    await upsertEntity('standing', standingKey(current), current);
    if (current.id !== null) await upsertEntity('task', current.id, current);
  }
  const res = await authedWrite<{ taskStatus: Partial<TaskRow> }>(
    'POST',
    `/api/v1/task-status/${current.id}/notes`,
    { notes },
  );
  const merged = { ...current, ...res.taskStatus } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
}

/** Put a missing report into that clinic's Drive folder, then show it here. */
export async function addMissingReport(row: TaskRow, file: File): Promise<void> {
  const uploaded = await uploadTaskReport(row.task_definition_id, file);
  const merged = {
    ...row,
    ...(uploaded.taskStatus || {}),
    report_url: uploaded.file.url,
    attachment_url: uploaded.file.url,
    attachment_name: uploaded.file.name,
    report_slot: uploaded.report_slot ?? row.report_slot,
  } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
}

export async function addTaskScan(row: TaskRow, file: File): Promise<void> {
  const attachment = await uploadTaskFile(row.task_definition_id, file, row.period_start);
  const files = [...(row.files ?? []), attachment];
  const merged = { ...row, files } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
  await refetchResource<TaskProfileData>(
    taskProfileKey(row.task_definition_id, row.period_start),
    () => fetchTaskProfile(row.task_definition_id, row.period_start),
  );
}

/**
 * Attach a link to a file the manager already holds in Drive. Owner,
 * 2026-09-14: "managers should absolutely be allowed to upload their own drive
 * files to weekly or monthly tasks or daily tasks for that matter." Same
 * endpoint the task page uses (a link, never a copy — this app holds no
 * files); the row and any open profile both learn about it.
 */
export async function addTaskLink(row: TaskRow, url: string, name: string): Promise<void> {
  await authedWrite('POST', `/api/v1/task-definitions/${row.task_definition_id}/attachments`, { url, name, periodStart: row.period_start });
  const profile = await fetchTaskProfile(row.task_definition_id, row.period_start);
  const files = (profile.attachments ?? []).map((a) => ({ id: a.id, url: a.url, name: a.name }));
  const merged = { ...row, files } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
  await refetchResource<TaskProfileData>(
    taskProfileKey(row.task_definition_id, row.period_start),
    () => fetchTaskProfile(row.task_definition_id, row.period_start),
  );
}

export async function dropTaskScan(row: TaskRow, fileId: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/task-attachments/${fileId}`);
  const files = (row.files ?? []).filter((f) => f.id !== fileId);
  const merged = { ...row, files } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
  await refetchResource<TaskProfileData>(
    taskProfileKey(row.task_definition_id, row.period_start),
    () => fetchTaskProfile(row.task_definition_id, row.period_start),
  );
}

/** Attach a report link the caller already has. Storing a URL, not uploading a
 * file — there are no Drive credentials in this stack. */
export async function attachReport(row: TaskRow, reportUrl: string): Promise<void> {
  const res = await authedWrite<{ taskStatus: Partial<TaskRow> }>(
    'POST',
    `/api/v1/task-status/${row.id}/report`,
    { reportUrl },
  );
  const merged = { ...row, ...res.taskStatus } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
}

/** Clear a task's report link. */
export async function clearReport(row: TaskRow): Promise<void> {
  const res = await authedWrite<{ taskStatus: Partial<TaskRow> }>(
    'DELETE',
    `/api/v1/task-status/${row.id}/report`,
  );
  const merged = { ...row, ...res.taskStatus, report_url: null } as TaskRow;
  await upsertEntity('standing', standingKey(merged), merged);
  if (merged.id !== null) await upsertEntity('task', merged.id, merged);
}

/** Add a task to ONE clinic's standing list. The other three are not touched. */
export async function addStandingTask(
  cadence: string, name: string, dueRule: string, clinic: string,
  extras?: { instructions?: string; report_label?: string; duration?: string },
): Promise<TaskRow> {
  const res = await authedWrite<{ taskDefinition: { id: number; name: string; cadence: string; due_rule: string; duration?: string | null; location_code?: string; report_driven?: boolean } }>(
    'POST', '/api/v1/task-definitions', {
      name, cadence, dueRule, locationCode: clinic,
      ...(extras?.duration ? { duration: extras.duration } : {}),
    },
  );
  const def = res.taskDefinition;
  const row: TaskRow = {
    id: null,
    task_definition_id: def.id,
    definition_name: def.name,
    cadence: def.cadence as TaskRow['cadence'],
    due_rule: def.due_rule,
    duration: def.duration ?? extras?.duration ?? null,
    automated: false,
    location_code: def.location_code || clinic,
    task_period_id: null,
    period_label: '',
    period_start: '',
    period_end: '',
    status: 'NOT_STARTED',
    completed_by: null,
    completed_at: null,
    verified_by: null,
    verified_at: null,
    sent_back_reason: null,
    report_url: null,
    notes: null,
    report_driven: def.report_driven ?? false,
    updated_at: null,
  };
  await upsertEntity('standing', standingKey(row), row);
  const patch: TaskProfilePatch = {};
  if (extras?.instructions) patch.instructions = extras.instructions;
  if (extras?.report_label !== undefined) patch.report_label = extras.report_label;
  if (Object.keys(patch).length > 0) {
    await saveTaskProfile(def.id, patch, [row]);
  }
  return getStandingTable()[standingKey(row)] ?? row;
}

function blankTask(cadence: TaskRow['cadence'], clinic: string): TaskRow {
  return {
    id: null,
    task_definition_id: 0,
    definition_name: '',
    cadence,
    due_rule: '',
    duration: cadence === 'MANAGER' ? 'WEEKLY' : null,
    automated: false,
    location_code: clinic,
    task_period_id: null,
    period_label: '',
    period_start: '',
    period_end: '',
    status: 'NOT_STARTED',
    completed_by: null,
    completed_at: null,
    verified_by: null,
    verified_at: null,
    sent_back_reason: null,
    report_url: null,
    notes: null,
    report_driven: false,
    updated_at: null,
  };
}

/** Rename a standing task or change when it is due. */
export async function editStandingTask(
  definitionId: number, name: string, dueRule: string, rows: TaskRow[],
  duration?: string | null,
): Promise<void> {
  const body: { name: string; dueRule: string; duration?: string } = { name, dueRule };
  if (duration) body.duration = duration;
  const res = await authedWrite<{ taskDefinition: { name: string; due_rule: string; duration?: string | null } }>(
    'PATCH', `/api/v1/task-definitions/${definitionId}`, body,
  );
  for (const row of rows) {
    await upsertEntity('standing', standingKey(row), {
      ...row,
      definition_name: res.taskDefinition.name,
      due_rule: res.taskDefinition.due_rule,
      duration: res.taskDefinition.duration ?? duration ?? row.duration,
    });
  }
}

/** What the profile editor may change. `undefined` means "not touched"; `''`
 * means "clear it" — the server tells those two apart deliberately, so this
 * type must not collapse them into one optional string that a caller sets to
 * '' when it meant to leave it alone. */
export interface TaskProfilePatch {
  name?: string;
  dueRule?: string;
  duration?: string;
  instructions?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  report_label?: string;
  reportDriven?: boolean;
}

/**
 * Save a task's instructions and/or its attachment link.
 *
 * The response carries THIS clinic's definition. After L-29 a definition is
 * not shared, so only the rows for this id are updated.
 */
export async function saveTaskProfile(
  definitionId: number, patch: TaskProfilePatch, rows: TaskRow[],
): Promise<void> {
  const res = await authedWrite<{
    taskDefinition: {
      name: string; due_rule: string; duration?: string | null;
      instructions: string | null;
      attachment_url: string | null;
      attachment_name: string | null;
      instructions_updated_at: string | null;
      instructions_updated_by: string | null;
      report_label: string | null;
      report_driven?: boolean;
    };
  }>('PATCH', `/api/v1/task-definitions/${definitionId}`, patch);
  const def = res.taskDefinition;
  for (const row of rows) {
    await upsertEntity('standing', standingKey(row), {
      ...row,
      definition_name: def.name,
      due_rule: def.due_rule,
      duration: def.duration ?? row.duration,
      instructions: def.instructions,
      attachment_url: def.attachment_url,
      attachment_name: def.attachment_name,
      instructions_updated_at: def.instructions_updated_at,
      instructions_updated_by: def.instructions_updated_by,
      report_label: def.report_label,
      report_driven: def.report_driven ?? row.report_driven,
      report_slot: reportLabelForTask(def.name, row.location_code, def.report_label),
    });
  }
  const cadence = rows[0]?.cadence;
  if (cadence && Object.prototype.hasOwnProperty.call(patch, 'report_label')) {
    await refreshStandingReportSlots(cadence);
  }
}

/** Pull this cadence again so a newly set report gets its filed file. */
async function refreshStandingReportSlots(cadence: string): Promise<void> {
  const fresh = await authedGet<{ tasks: TaskRow[] }>(
    cadence === 'WEEKLY'
      ? '/api/v1/task-definitions?cadence=WEEKLY&drive=0'
      : `/api/v1/task-definitions?cadence=${cadence}`,
  );
  for (const t of fresh.tasks || []) {
    const prev = getStandingTable()[standingKey(t)];
    if (!prev) continue;
    await upsertEntity('standing', standingKey(t), {
      ...prev,
      definition_name: t.definition_name,
      due_rule: t.due_rule,
      instructions: t.instructions,
      report_label: t.report_label,
      report_driven: t.report_driven ?? prev.report_driven,
      report_slot: t.report_slot,
      attachment_url: t.attachment_url ?? null,
      attachment_name: t.attachment_name ?? null,
      report_landed_at: t.report_landed_at ?? null,
      filed_report: t.filed_report ?? null,
    });
  }
}

/**
 * Take a task off the standing list.
 *
 * A RETIRE, not a delete — the server soft-retires it (migration 027) because
 * every historic completed row points at this definition. The rows already in
 * the completed table are untouched and stay readable; only the standing list
 * loses it.
 */
export async function retireStandingTask(definitionId: number, rows: TaskRow[]): Promise<void> {
  await authedWrite('DELETE', `/api/v1/task-definitions/${definitionId}`);
  // The whole table is rewritten rather than upserted, because this is the one
  // change that REMOVES rows — `upsertEntity` has no way to express that, and
  // leaving the row in the store would keep a retired task on screen until the
  // next reload.
  const next = { ...getStandingTable() };
  for (const row of rows) delete next[standingKey(row)];
  await putEntityTable('standing', next);
}

// --- the standing list's three modes ---------------------------------------
//
// Owner, 2026-08-16: "these buttons Edit the whol task list not just one item
// so do this and remove the inline edit remove. add remove also. when you click
// remove you get little x next to each line and you can delete one."
//
// So the card is in exactly one of three states at a time, and the controls in
// its heading say which. A mode, not a set of independent toggles: editing
// while removing would put an × beside a half-typed name.
export type ListMode = 'view' | 'edit' | 'remove';

/** One row's pending edit while the whole list is in edit mode. */
export interface RowDraft { name: string; dueRule: string; duration: string }

/**
 * The rows that actually CHANGED, as `{ id, name, dueRule }`.
 *
 * Pulled out as a pure function because it is the rule that keeps Save honest:
 * with 55 tasks on a monthly list, PATCHing every row on every Save would be 55
 * writes, 55 event rows and 55 chances to fail, to record that one due date
 * moved. Trimmed before comparing, so adding a trailing space is not an edit.
 */
export function changedDrafts(
  drafts: Record<number, RowDraft>, rows: TaskRow[],
): { id: number; name: string; dueRule: string; duration: string }[] {
  const out: { id: number; name: string; dueRule: string; duration: string }[] = [];
  const seen = new Set<number>();
  for (const row of rows) {
    if (seen.has(row.task_definition_id)) continue;
    seen.add(row.task_definition_id);
    const draft = drafts[row.task_definition_id];
    if (!draft) continue;
    const name = draft.name.trim();
    const dueRule = draft.dueRule.trim();
    const duration = draft.duration ?? row.duration ?? '';
    const rowDuration = row.duration ?? '';
    if (name === row.definition_name && dueRule === row.due_rule && duration === rowDuration) continue;
    out.push({ id: row.task_definition_id, name, dueRule, duration });
  }
  return out;
}

/** The starting drafts when edit mode opens: every visible row, prefilled from
 * its RAW stored values. One entry per DEFINITION — and after L-29 each
 * clinic has its own definition, so editing due-by here cannot rewrite another
 * clinic's copy. */
export function draftsFor(rows: TaskRow[]): Record<number, RowDraft> {
  const out: Record<number, RowDraft> = {};
  for (const row of rows) {
    out[row.task_definition_id] = {
      name: row.definition_name,
      dueRule: row.due_rule,
      duration: row.duration ?? '',
    };
  }
  return out;
}

// --- the task profile (owner, 2026-08-16) ----------------------------------
//
// "A profile page per task, where you write the instructions. get this done
// asap.. has to be there" — and earlier, "Clicking the task should open an
// explanation/details box… explain exactly what needs to be done."
//
// It follows People's roster -> profile idiom exactly (client/src/pages/
// People.tsx): a `selected` id on the page, a "Back to Tasks" control in the
// page header, and no router route — Tasks has never had one, and inventing one
// here would make this the only page with two ways of being somewhere.

/** Who may WRITE a task's instructions. The server checks this again on every
 * request (INV-3); this only decides whether it is worth drawing a control that
 * would be refused. A manager READS the instructions and cannot change them. */
function canEditInstructions(): boolean {
  const role = session.value.me?.role;
  return role === 'OWNER' || role === 'DIRECTOR';
}

function canSetReport(): boolean {
  return session.value.me?.role === 'OWNER';
}


// --- the task profile's notes and attachments (owner, 2026-08-17) -----------
//
// "on the task window where we have instructons we need to be able to enter
// notes thee and attache things and also delets or edit."
//
// 033 gave a task ONE instructions field and ONE attachment link, both
// overwritten in place: nowhere to record what happened this week, and a second
// file meant losing the first. These two lists are the answer, and they follow
// the employee-profile notes idiom exactly (People.tsx's NoteRow) — inline
// "Delete? Yes / Cancel", never `window.confirm`, which freezes the tab
// (docs/known-traps.md).

export interface TaskNote {
  id: number;
  task_definition_id: number;
  body: string;
  author: string;
  created_at: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface TaskAttachment {
  id: number;
  task_definition_id: number;
  url: string;
  name: string | null;
  added_by: string;
  added_at: string;
}

interface TaskProfileData {
  notes: TaskNote[];
  attachments: TaskAttachment[];
}

// One week's (or month's) notes-and-files view (082, owner 2026-09-24: "a
// week's attachment belongs in that week"). The key carries the week, so
// last week's files never show on this week's task.
const taskProfileKey = (definitionId: number, periodStart: string): string => `task-profile:${definitionId}:${periodStart}`;
export const taskProfileUrl = (definitionId: number, periodStart: string): string =>
  `/api/v1/task-definitions/${definitionId}/profile?period=${encodeURIComponent(periodStart)}`;
const fetchTaskProfile = (definitionId: number, periodStart: string): Promise<TaskProfileData> =>
  authedGet<TaskProfileData>(taskProfileUrl(definitionId, periodStart));

/** A date+author line, from a timestamp that may be null or unparseable.
 *
 * The date is the CLINIC's (lib/clinicDay.ts), not the reader's: `created_at`
 * is a UTC instant, and formatted in the browser's zone a note written at
 * 6:35 PM in Florida stamped itself the NEXT day for anyone further east —
 * seen on this very page the day it shipped. */
function stampLine(who: string, when: string): string {
  const d = new Date(when);
  return Number.isNaN(d.getTime()) ? who : `${formatDate(clinicNow(d))} — ${who}`;
}

function TaskNoteRow(
  { note, definitionId, periodStart, canEdit }:
  { note: TaskNote; definitionId: number; periodStart: string; canEdit: boolean },
): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const [confirming, setConfirming] = useState(false);
  const edit = useWrite();
  const del = useWrite();
  const reload = () => refetchResource<TaskProfileData>(
    taskProfileKey(definitionId, periodStart), () => fetchTaskProfile(definitionId, periodStart),
  );

  async function saveEdit(): Promise<void> {
    await edit.run(async () => {
      await authedWrite('PATCH', `/api/v1/task-notes/${note.id}`, { body });
      await reload();
    }, () => setEditing(false));
  }

  async function confirmDelete(): Promise<void> {
    await del.run(async () => {
      await authedWrite('DELETE', `/api/v1/task-notes/${note.id}`);
      await reload();
    });
  }

  const metaLine = (
    <div class="empty">
      {stampLine(note.author, note.created_at)}
      {note.updated_by ? ' — edited' : ''}
    </div>
  );

  return (
    <li data-testid={`task-note-${note.id}`}>
      {editing ? (
        <>
          {metaLine}
          <textarea
            class="notes-entry"
            aria-label={`Edit note ${note.id}`}
            rows={8}
            value={body}
            onInput={(ev: Event) => setBody((ev.target as HTMLTextAreaElement).value)}
          />
          <button type="button" class="btn primary" onClick={() => void saveEdit()} disabled={edit.busy}>
            {edit.busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button" class="btn"
            onClick={() => { setBody(note.body); setEditing(false); }}
            disabled={edit.busy}
          >
            Cancel
          </button>
          {edit.error ? <small class="bad" role="alert">{edit.error}</small> : null}
        </>
      ) : (
        <>
          <div class="section-head-row">
            {metaLine}
            {canEdit ? (
              <div class="section-head-actions">
                <button type="button" class="btn" onClick={() => setEditing(true)}>Edit</button>
                {confirming ? (
                  <span data-testid={`task-note-${note.id}-confirm`}>
                    Delete?{' '}
                    <button type="button" class="btn" onClick={() => void confirmDelete()} disabled={del.busy}>
                      {del.busy ? 'Deleting…' : 'Yes'}
                    </button>{' '}
                    <button type="button" class="btn" onClick={() => setConfirming(false)} disabled={del.busy}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" class="btn" onClick={() => setConfirming(true)}>Delete</button>
                )}
              </div>
            ) : null}
          </div>
          {/* pre-wrap, so the line breaks he types are the ones he reads back. */}
          <p class="instructions-text">{note.body}</p>
          {del.error ? <small class="bad" role="alert">{del.error}</small> : null}
        </>
      )}
    </li>
  );
}

function TaskAttachmentRow(
  { file, definitionId, periodStart, canEdit }:
  { file: TaskAttachment; definitionId: number; periodStart: string; canEdit: boolean },
): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const del = useWrite();

  async function confirmDelete(): Promise<void> {
    await del.run(async () => {
      await authedWrite('DELETE', `/api/v1/task-attachments/${file.id}`);
      await refetchResource<TaskProfileData>(
        taskProfileKey(definitionId, periodStart), () => fetchTaskProfile(definitionId, periodStart),
      );
      const row = Object.values(getStandingTable()).find((r) => r.task_definition_id === definitionId);
      if (row) {
        await upsertEntity('standing', standingKey(row), {
          ...row, files: (row.files ?? []).filter((f) => f.id !== file.id),
        });
      }
    });
  }

  return (
    <li data-testid={`task-attachment-${file.id}`}>
      <div class="section-head-row">
        <div>
          <TaskFileLink kind="attachment" id={file.id} href={file.url} name={file.name}>{file.name || file.url}</TaskFileLink>
          <div class="empty">{stampLine(file.added_by, file.added_at)}</div>
        </div>
        {canEdit ? (
          <div class="section-head-actions">
            {confirming ? (
              <span data-testid={`task-attachment-${file.id}-confirm`}>
                Delete?{' '}
                <button type="button" class="btn" onClick={() => void confirmDelete()} disabled={del.busy}>
                  {del.busy ? 'Deleting…' : 'Yes'}
                </button>{' '}
                <button type="button" class="btn" onClick={() => setConfirming(false)} disabled={del.busy}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" class="btn" onClick={() => setConfirming(true)}>Delete</button>
            )}
          </div>
        ) : null}
      </div>
      {del.error ? <small class="bad" role="alert">{del.error}</small> : null}
    </li>
  );
}

/** The two lists, under the instructions. */
/**
 * ONE way to attach (owner, 2026-09-24: "Attach should pop up a box which lets
 * you attach or drag in a file or add the link"). Drag a file in or choose
 * one, paste a Drive link, or pick from the clinic's own Drive — whatever is
 * added belongs to the week or month on screen.
 */
function AttachBox(
  { title, definitionId, onFile, onLink, onClose, testPrefix = '' }:
  {
    title: string;
    definitionId: number;
    onFile: (file: File) => Promise<void>;
    onLink: (url: string, name: string) => Promise<void>;
    onClose: () => void;
    testPrefix?: string;
  },
): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [link, setLink] = useState('');
  const [linkName, setLinkName] = useState('');
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function run(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="note-drawer-root" data-testid={`${testPrefix}attach-box`}>
      <button type="button" class="note-drawer-backdrop" aria-label="Close" onClick={onClose} />
      <div class="note-drawer attach-box" role="dialog" aria-label={`Attach to ${title}`}>
        <div class="note-drawer-head">
          <h3>Attach to {title}</h3>
          <span class="sp" />
          <button type="button" class="note-drawer-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div class="attach-box-body">
          <label
            class={dragging ? 'attach-drop dragging' : 'attach-drop'}
            data-testid={`${testPrefix}attach-drop`}
            onDragOver={(e: DragEvent) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e: DragEvent) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer?.files?.[0];
              if (file) void run(() => onFile(file));
            }}
          >
            <strong>{busy ? 'Adding…' : 'Drag a file here'}</strong>
            <span class="muted">or click to choose one from this computer</span>
            <input
              type="file"
              class="visually-hidden"
              aria-label="Add a file"
              data-testid={`${testPrefix}task-file-upload`}
              disabled={busy}
              onChange={(ev: Event) => {
                const input = ev.target as HTMLInputElement;
                const file = input.files?.[0];
                if (file) void run(() => onFile(file)).finally(() => { input.value = ''; });
              }}
            />
          </label>

          <div class="attach-or">or paste a link</div>
          <div class="attach-row">
            <input
              type="text" class="attach-link" aria-label="Drive link"
              placeholder="https://drive.google.com/…"
              value={link}
              onInput={(ev: Event) => setLink((ev.target as HTMLInputElement).value)}
            />
            <input
              type="text" class="attach-name" aria-label="Shown as"
              placeholder="What to call it"
              value={linkName}
              onInput={(ev: Event) => setLinkName((ev.target as HTMLInputElement).value)}
            />
            <button
              type="button" class="btn primary" data-testid={`${testPrefix}add-task-attachment`}
              disabled={busy || !link.trim()}
              onClick={() => void run(() => onLink(link.trim(), linkName.trim()))}
            >
              Add link
            </button>
          </div>

          <div class="attach-or">or pick from your clinic's Drive</div>
          <button type="button" class="btn" data-testid={`${testPrefix}task-drive-pick`} onClick={() => setBrowsing(!browsing)}>
            {browsing ? 'Close Drive' : 'Open my Drive'}
          </button>
          {browsing ? (
            <DrivePicker
              definitionId={definitionId}
              busy={busy}
              onPick={(f) => { if (f.url) void run(() => onLink(f.url!, f.name)); }}
            />
          ) : null}

          {error ? <small class="bad" role="alert">{error}</small> : null}
        </div>
      </div>
    </div>
  );
}

export interface DriveItem { id: string; name: string; url: string | null; modifiedTime: string | null }
interface DriveListing { folderId: string; atRoot: boolean; folders: DriveItem[]; files: DriveItem[] }

/** "Pick from my Drive" (owner, 2026-09-24; idea #14, ledger N-015): the
 * task clinic's own shared drive, one folder at a time. Clicking a file
 * attaches its link to the week on screen — nothing is copied. */
function DrivePicker(
  { definitionId, onPick, busy }:
  { definitionId: number; onPick: (file: DriveItem) => void; busy: boolean },
): JSX.Element {
  const [trail, setTrail] = useState<DriveItem[]>([]);
  const folder = trail.length ? trail[trail.length - 1].id : null;
  const [listing, setListing] = useState<DriveListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setListing(null);
    setError(null);
    authedGet<DriveListing>(`/api/v1/task-definitions/${definitionId}/drive${folder ? `?folder=${encodeURIComponent(folder)}` : ''}`)
      .then((l) => { if (alive) setListing(l); })
      .catch((err: unknown) => { if (alive) setError(err instanceof Error ? err.message : String(err)); });
    return () => { alive = false; };
  }, [definitionId, folder]);

  return (
    <div class="drive-picker" data-testid="drive-picker">
      <div class="drive-picker-trail">
        <button type="button" class="link" onClick={() => setTrail([])}>My Drive</button>
        {trail.map((t, i) => (
          <span key={t.id}> / <button type="button" class="link" onClick={() => setTrail(trail.slice(0, i + 1))}>{t.name}</button></span>
        ))}
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}
      {!listing && !error ? <p class="muted">Loading your Drive…</p> : null}
      {listing ? (
        <ul class="drive-picker-list">
          {listing.folders.map((f) => (
            <li key={f.id}>
              <button type="button" class="link" data-testid={`drive-folder-${f.id}`} onClick={() => setTrail([...trail, f])}>
                {f.name} /
              </button>
            </li>
          ))}
          {listing.files.map((f) => (
            <li key={f.id}>
              <button type="button" class="link" disabled={busy} data-testid={`drive-file-${f.id}`} onClick={() => onPick(f)}>
                {f.name}
              </button>
              {f.modifiedTime ? <span class="muted"> · {formatDate(clinicNow(new Date(f.modifiedTime)))}</span> : null}
            </li>
          ))}
          {!listing.folders.length && !listing.files.length ? <li class="muted">This folder is empty.</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

function TaskNotesAndFiles(
  { definitionId, periodStart, canEdit }: { definitionId: number; periodStart: string; canEdit: boolean },
): JSX.Element {
  const [noteBody, setNoteBody] = useState('');
  const addNote = useWrite();
  const [picking, setPicking] = useState(false);

  const data = useResource<TaskProfileData>(
    taskProfileKey(definitionId, periodStart), () => fetchTaskProfile(definitionId, periodStart),
  );
  const reload = () => refetchResource<TaskProfileData>(
    taskProfileKey(definitionId, periodStart), () => fetchTaskProfile(definitionId, periodStart),
  );

  async function submitNote(): Promise<void> {
    const body = noteBody.trim();
    if (!body) return;
    await addNote.run(async () => {
      await authedWrite('POST', `/api/v1/task-definitions/${definitionId}/notes`, { body });
      await reload();
    }, () => setNoteBody(''));
  }


  if (data.status === 'loading') return <EmptyState label="Loading notes and files…" />;
  if (data.status === 'fault') {
    return <FaultState name="could not load this task's notes" detail={data.error} />;
  }

  // Defaulted, not asserted. A response missing either key — an older API
  // still deploying, a partial answer — must not blank the whole task page;
  // it should show an empty list and let the rest of the card stand.
  const notes = data.data?.notes ?? [];
  const attachments = data.data?.attachments ?? [];

  return (
    <>
      <section data-testid="task-notes">
        <div class="section-head">
          <div class="section-head-row">
            <h3>Notes</h3>
          </div>
        </div>

        {canEdit ? (
          <div>
            {/* A big box, for the same reason the instructions box is big: a
                note typed into three visible lines is a box you fight. */}
            <textarea
              class="notes-entry"
              rows={6}
              aria-label="New note"
              placeholder="What happened, what to watch for, what changed."
              value={noteBody}
              onInput={(ev: Event) => setNoteBody((ev.target as HTMLTextAreaElement).value)}
            />
            <button
              type="button" class="btn primary" data-testid="add-task-note"
              onClick={() => void submitNote()} disabled={addNote.busy || !noteBody.trim()}
            >
              {addNote.busy ? 'Saving…' : 'Add note'}
            </button>
            {addNote.error ? <small class="bad" role="alert">{addNote.error}</small> : null}
          </div>
        ) : null}

        {notes.length === 0 ? (
          <p class="muted" data-testid="task-notes-empty">No notes yet.</p>
        ) : (
          <ul class="notes-list">
            {notes.map((n) => (
              <TaskNoteRow key={n.id} note={n} definitionId={definitionId} periodStart={periodStart} canEdit={canEdit} />
            ))}
          </ul>
        )}
      </section>

      <section data-testid="task-attachments">
        <div class="section-head">
          <div class="section-head-row">
            <h3>Attachments</h3>
            {canEdit ? (
              <div class="section-head-actions">
                <button type="button" class="btn" data-testid="task-attach" onClick={() => setPicking(true)}>
                  Attach
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {canEdit && picking ? (
          <AttachBox
            title="this week"
            definitionId={definitionId}
            onClose={() => setPicking(false)}
            onFile={async (file) => {
              const attachment = await uploadTaskFile(definitionId, file, periodStart);
              await reload();
              const row = Object.values(getStandingTable()).find((r) => r.task_definition_id === definitionId);
              if (row) {
                await upsertEntity('standing', standingKey(row), {
                  ...row, files: [...(row.files ?? []), attachment],
                });
              }
            }}
            onLink={async (link, name) => {
              await authedWrite(
                'POST', `/api/v1/task-definitions/${definitionId}/attachments`,
                { url: link, name, periodStart },
              );
              await reload();
            }}
          />
        ) : null}

        {attachments.length === 0 ? (
          <p class="muted" data-testid="task-attachments-empty">No files attached.</p>
        ) : (
          <ul class="notes-list">
            {attachments.map((f) => (
              <TaskAttachmentRow key={f.id} file={f} definitionId={definitionId} periodStart={periodStart} canEdit={canEdit} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/**
 * One task's own page: what it is, when it is due, what it means, and the file
 * that goes with it.
 *
 * `row` is any one of this definition's standing rows — the profile fields live
 * on the DEFINITION, so all four clinics' rows carry the same values and it does
 * not matter which one arrives here.
 */
export function TaskProfile(
  { row, siblings, onBack, creating, onCreated }:
  {
    row: TaskRow;
    siblings: TaskRow[];
    onBack: () => void;
    creating?: boolean;
    onCreated?: (id: number) => void;
  },
): JSX.Element {
  const [editing, setEditing] = useState(Boolean(creating));
  const [name, setName] = useState(row.definition_name);
  const [dueRule, setDueRule] = useState(row.due_rule);
  const [duration, setDuration] = useState(row.duration ?? (row.cadence === 'MANAGER' ? 'WEEKLY' : ''));
  const [instructions, setInstructions] = useState(row.instructions ?? '');
  const [reportShort, setReportShort] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const canEdit = canEditInstructions();
  const canEditBoard = canEditStandingList(row.cadence);
  const canReport = canSetReport();
  const reportSlot = row.report_slot
    ?? reportLabelForTask(row.definition_name, row.location_code, row.report_label);

  function startEdit(): void {
    setName(row.definition_name);
    setDueRule(row.due_rule);
    setDuration(row.duration ?? (row.cadence === 'MANAGER' ? 'WEEKLY' : ''));
    setInstructions(row.instructions ?? '');
    setReportShort(reportShortFromLabel(reportSlot, row.location_code));
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    startEdit();
    setEditing(false);
  }

  async function save(): Promise<void> {
    if (creating) {
      if (!name.trim() || !dueRule.trim()) {
        setError('Name and due are required.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const extras: { instructions?: string; report_label?: string; duration?: string } = {};
        if (canEdit && instructions.trim()) extras.instructions = instructions;
        if (canReport && reportShort.trim()) {
          extras.report_label = `${row.location_code} ${reportShort.trim()}`;
        }
        if (row.cadence === 'MANAGER') extras.duration = duration || 'WEEKLY';
        const created = await addStandingTask(
          row.cadence, name.trim(), dueRule.trim(), row.location_code, extras,
        );
        onCreated?.(created.task_definition_id);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
      return;
    }

    const patch: TaskProfilePatch = {};
    if (name.trim() !== row.definition_name) patch.name = name.trim();
    if (dueRule.trim() !== row.due_rule) patch.dueRule = dueRule.trim();
    if (row.cadence === 'MANAGER' && duration !== (row.duration ?? '')) patch.duration = duration;
    if (canEdit && instructions !== (row.instructions ?? '')) patch.instructions = instructions;
    if (canReport) {
      const currentShort = reportShortFromLabel(reportSlot, row.location_code);
      if (reportShort.trim() !== currentShort) {
        patch.report_label = reportShort.trim()
          ? `${row.location_code} ${reportShort.trim()}`
          : '';
      }
    }
    if (Object.keys(patch).length === 0) { setEditing(false); return; }

    setBusy(true);
    setError(null);
    try {
      await saveTaskProfile(row.task_definition_id, patch, siblings);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function removeTask(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await retireStandingTask(row.task_definition_id, siblings.length ? siblings : [row]);
      onBack();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div class="main" data-testid="task-profile-view">
      <div class="page-header">
        <button type="button" class="link" data-testid="back-to-tasks" onClick={onBack}>
          Back to Tasks
        </button>
      </div>

      <div class="card" data-testid="task-profile">
        <div class="section-head">
          <div class="section-head-row">
            <h2>{creating ? 'New task' : editing ? 'Edit task' : row.definition_name}</h2>
            <div class="section-head-actions">
              {(canEdit || canEditBoard) && !editing && !creating && (
                <button type="button" class="btn" data-testid="edit-instructions" onClick={startEdit}>
                  Edit
                </button>
              )}
              {canEditBoard && !creating && !confirmingDelete && (
                <button
                  type="button"
                  class="btn"
                  data-testid="delete-task"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={busy}
                >
                  Delete
                </button>
              )}
              {canEditBoard && !creating && confirmingDelete && (
                <span data-testid="delete-task-confirm">
                  Delete?{' '}
                  <button type="button" class="btn" onClick={() => void removeTask()} disabled={busy}>
                    {busy ? 'Deleting…' : 'Yes'}
                  </button>{' '}
                  <button type="button" class="btn" onClick={() => setConfirmingDelete(false)} disabled={busy}>
                    Cancel
                  </button>
                </span>
              )}
              {editing && (
                <>
                  <button type="button" class="btn" onClick={creating ? onBack : cancelEdit} disabled={busy}>Cancel</button>
                  <button
                    type="button"
                    class="btn primary"
                    onClick={() => void save()}
                    disabled={busy}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
          {editing && (canEditBoard || creating) ? (
            <div class="standing-editor" data-testid="task-profile-fields">
              <label>
                Name
                <input
                  type="text"
                  aria-label="Task name"
                  value={name}
                  onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
                />
              </label>
              <p class="muted">{CADENCE_LABEL[row.cadence]} — cadence cannot be changed</p>
              <label>
                Due
                <input
                  type="text"
                  aria-label="Due by"
                  value={dueRule}
                  onInput={(e: Event) => setDueRule((e.target as HTMLInputElement).value)}
                />
              </label>
              {row.cadence === 'MANAGER' ? (
                <label>
                  Duration
                  <select
                    aria-label="Duration"
                    value={duration}
                    onChange={(e: Event) => setDuration((e.target as HTMLSelectElement).value)}
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </select>
                </label>
              ) : null}
            </div>
          ) : (
            <p>
              {CADENCE_LABEL[row.cadence]}
              {row.due_rule ? ` — due ${row.due_rule}` : ''}
              {row.cadence === 'MANAGER' && row.duration
                ? ` — ${DURATION_LABEL[row.duration] ?? row.duration}`
                : ''}
            </p>
          )}
        </div>

        <section data-testid="task-instructions">
          <div class="section-head">
            <div class="section-head-row">
              <h3>Instructions</h3>
            </div>
          </div>
          {error ? <small class="bad" role="alert">{error}</small> : null}

          {editing && canEdit ? (
            <textarea
              class="instructions-box"
              rows={16}
              aria-label="Instructions"
              placeholder="What exactly needs to be done, step by step."
              value={instructions}
              onInput={(e: Event) => setInstructions((e.target as HTMLTextAreaElement).value)}
            />
          ) : row.instructions ? (
            <InstructionsText text={row.instructions} known={knownTaskFiles(row)} testId="instructions-text" />
          ) : (
            <p class="muted" data-testid="instructions-empty">No instructions yet.</p>
          )}

          {!editing && row.instructions_updated_by ? (
            <small class="muted">
              Last written by {row.instructions_updated_by}
              {row.instructions_updated_at && !Number.isNaN(new Date(row.instructions_updated_at).getTime())
                ? ` on ${formatDate(clinicNow(new Date(row.instructions_updated_at)))}`
                : ''}
            </small>
          ) : null}
        </section>

        <section data-testid="task-report-slot">
          <div class="section-head">
            <div class="section-head-row">
              <h3>Report</h3>
              {!editing && !row.attachment_url && !row.report_url ? (
                <div class="section-head-actions">
                  <MissingReportAdd row={row} testId="task-report-add" />
                </div>
              ) : null}
            </div>
          </div>
          {editing && canReport ? (
            <label>
              Which report
              <select
                aria-label="Report for this task"
                data-testid="task-report-pick"
                value={reportShort}
                onChange={(e: Event) => setReportShort((e.target as HTMLSelectElement).value)}
              >
                <option value="">No report</option>
                {MEEVO_REPORTS.map((short) => (
                  <option key={short} value={short}>{short}</option>
                ))}
              </select>
            </label>
          ) : reportSlot ? (
            <div class="section-head-row">
              <p data-testid="task-report-slot-label">{reportSlot}</p>
                {row.attachment_url ? (
                  <span class="report-landed">
                    <TaskFileLink
                      class="drive-mark"
                      kind={reportProxy(row)?.kind}
                      id={reportProxy(row)?.id}
                      href={row.attachment_url}
                      name={row.attachment_name}
                      testId="task-report-slot-file"
                      ariaLabel={`Open ${row.attachment_name || reportSlot} in Google Drive`}
                      title={row.attachment_name || reportSlot}
                    >
                      <span class="sr-only">{row.attachment_name || reportSlot}</span>
                    </TaskFileLink>
                    {row.report_landed_at ? (
                      <small class="muted" data-testid="task-report-slot-landed">
                        {formatReportLanded(row.report_landed_at)}
                      </small>
                    ) : null}
                  </span>
                ) : null}
            </div>
          ) : (
            <p class="muted" data-testid="task-report-slot-none">No report for this task.</p>
          )}
        </section>

        {/* The 033 single-attachment field is RETIRED from this page (2026-08-17).
            It held one link, overwritten in place, and production had zero rows
            using it; the Attachments list below holds many, each deletable. The
            COLUMN stays in the database — retiring a field is not deleting the
            data that might be in it — but nothing draws it any more. */}

        {/* NOT `canEdit` — that gate is the INSTRUCTIONS gate (OWNER/DIRECTOR).
            Owner ruling 2026-08-17: "all can add notes and attachmetns managers
            also", so every signed-in person gets these controls. The server
            checks it again for real (INV-3) — src/task_profile.js. */}
        {!creating && TERMINAL_STATUSES.includes(row.status) ? (
          <section data-testid="task-completion">
            <h3>Completion</h3>
            <p class="good">{doneDetail(row)}</p>
            {row.notes ? <p data-testid="task-completion-notes">{row.notes}</p> : null}
          </section>
        ) : null}

        {creating ? null : <TaskNotesAndFiles definitionId={row.task_definition_id} periodStart={row.period_start} canEdit />}
        {creating ? null : <TaskHistory definitionId={row.task_definition_id} />}
      </div>
    </div>
  );
}

// --- notes, files and comments across periods (owner, 2026-11-02) ----------
// "why is there no way to see actual manager comments and such on finished
// tasks or uploaded files?"

export interface NoteFileItem {
  kind: 'note' | 'comment' | 'file';
  id: string;
  task_definition_id: string | number;
  task_name: string;
  text: string;
  url?: string;
  author: string | null;
  at: string | null;
}

export const notesFilesUrl = (clinic: string, start: string, end: string): string =>
  `/api/v1/task-notes-files?clinic=${encodeURIComponent(clinic)}&start=${start}&end=${end}`;

/** How many notes, comments and files each task has, by definition id. */
export function noteFileCounts(items: NoteFileItem[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const i of items) {
    const k = Number(i.task_definition_id);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

const KIND_LABEL: Record<NoteFileItem['kind'], string> = { note: 'Note', comment: 'Comment', file: 'File' };

function stampOf(at: string | null): string {
  const d = at ? new Date(at) : null;
  return d && !Number.isNaN(d.getTime()) ? formatDate(clinicNow(d)) : '';
}

/** Everything written on or attached to one clinic's tasks in one period. */
export function NotesFilesList({ items }: { items: NoteFileItem[] }): JSX.Element {
  if (items.length === 0) return <p class="muted" data-testid="notes-files-empty">No notes or files for this period.</p>;
  return (
    <ul class="notes-list" data-testid="notes-files-list">
      {items.map((i) => (
        <li key={`${i.kind}:${i.id}`} data-testid={`notes-files-${i.kind}`}>
          <strong>{i.task_name}</strong>{' '}
          <span class="muted">{KIND_LABEL[i.kind]}{i.author ? ` · ${i.author}` : ''}{stampOf(i.at) ? ` · ${stampOf(i.at)}` : ''}</span>
          <div>
            {i.kind === 'file' && i.url
              ? <TaskFileLink class="doc-link" kind="attachment" id={i.id} href={i.url} name={i.text} rel="noopener noreferrer">{i.text}</TaskFileLink>
              : i.text}
          </div>
        </li>
      ))}
    </ul>
  );
}

interface TaskHistoryData {
  notes: TaskNote[];
  attachments: (TaskAttachment & { period_start?: string | null })[];
  comments: { id: string; author: string | null; body: string; created_at: string; period_label: string }[];
  status_notes: { period_label: string; author: string | null; at: string | null; body: string }[];
}

/** Every period's notes, files and comments for one task, newest first. */
export function TaskHistoryView({ data }: { data: TaskHistoryData }): JSX.Element {
  const entries: { key: string; at: string | null; who: string | null; where: string; node: JSX.Element }[] = [];
  for (const n of data.notes ?? []) {
    entries.push({ key: `n${n.id}`, at: n.created_at, who: n.author, where: 'Note', node: <>{n.body}</> });
  }
  for (const c of data.comments ?? []) {
    entries.push({ key: `c${c.id}`, at: c.created_at, who: c.author, where: `Comment · ${c.period_label}`, node: <>{c.body}</> });
  }
  (data.status_notes ?? []).forEach((c, idx) => {
    entries.push({ key: `s${idx}`, at: c.at, who: c.author, where: `Completion note · ${c.period_label}`, node: <>{c.body}</> });
  });
  for (const f of data.attachments ?? []) {
    entries.push({
      key: `f${f.id}`, at: f.added_at, who: f.added_by,
      where: f.period_start ? `File · week of ${formatDay(f.period_start)}` : 'File',
      node: <TaskFileLink class="doc-link" kind="attachment" id={f.id} href={f.url} name={f.name} rel="noopener noreferrer">{f.name || f.url}</TaskFileLink>,
    });
  }
  entries.sort((a, b) => String(b.at ?? '').localeCompare(String(a.at ?? '')));
  if (entries.length === 0) return <p class="muted" data-testid="task-history-empty">Nothing recorded for this task yet.</p>;
  return (
    <ul class="notes-list" data-testid="task-history-list">
      {entries.map((e) => (
        <li key={e.key}>
          <span class="muted">{e.where}{e.who ? ` · ${e.who}` : ''}{stampOf(e.at) ? ` · ${stampOf(e.at)}` : ''}</span>
          <div>{e.node}</div>
        </li>
      ))}
    </ul>
  );
}

/** Lazy: nothing is fetched until the History section is opened. */
function TaskHistory({ definitionId }: { definitionId: number }): JSX.Element {
  const [open, setOpen] = useState(false);
  const data = useResource<TaskHistoryData>(
    open ? `task-history:${definitionId}` : null,
    () => authedGet<TaskHistoryData>(`/api/v1/task-definitions/${definitionId}/history`),
  );
  return (
    <section data-testid="task-history">
      <div class="section-head"><div class="section-head-row"><h3>History</h3></div></div>
      {!open ? (
        <button type="button" class="mini" data-testid="task-history-open" onClick={() => setOpen(true)}>
          Show every past period&apos;s notes and files
        </button>
      ) : data.status === 'loading' ? <EmptyState label="Loading history…" />
        : data.status === 'fault' ? <FaultState name="could not load this task's history" detail={data.error} />
          : <TaskHistoryView data={data.data} />}
    </section>
  );
}

// --- the standing list (top table) ----------------------------------------

/**
 * Report column: a Drive mark if the file is in the folder, nothing if it
 * is not. Click opens Drive. Nobody pastes a link here.
 */
/** Empty Report cell: Add the missing file. A mapped slot is not required. */
function reportExpected(row: TaskRow): boolean {
  return !row.attachment_url && !row.report_url;
}

function MissingReportAdd(
  { row, testId }:
  { row: TaskRow; testId: string },
): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await addMissingReport(row, file);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      input.value = '';
    }
  }

  return (
    <span class="report-add" onClick={(e: MouseEvent) => e.stopPropagation()}>
      <label class="btn" data-testid={`${testId}-label`}>
        {busy ? 'Adding…' : 'Add'}
        <input
          type="file"
          class="visually-hidden"
          aria-label={`Add report for ${row.definition_name}`}
          data-testid={testId}
          disabled={busy}
          onChange={(ev) => void pick(ev)}
        />
      </label>
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </span>
  );
}

function FilesCell({ row }: { row: TaskRow }): JSX.Element {
  const files = row.files ?? [];
  const [busy, setBusy] = useState(false);
  // Owner, 2026-09-14: a manager attaches her OWN Drive file from the row she
  // completes the task on. 2026-09-24: one Attach button, one box — drag in a
  // file, choose one, paste a link or pick from the clinic's Drive.
  const [attaching, setAttaching] = useState(false);

  return (
    <div class="task-files" onClick={(e: MouseEvent) => e.stopPropagation()}>
      {files.map((f) => (
        <span key={f.id} class="task-file" data-testid={`task-file-${f.id}`}>
          <TaskFileLink
            class="doc-link"
            kind="attachment"
            id={f.id}
            href={f.url}
            name={f.name}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            {f.name || 'File'}
          </TaskFileLink>
          {' '}
          <button
            type="button"
            class="btn"
            aria-label={`Remove ${f.name || 'file'}`}
            data-testid={`task-file-remove-${f.id}`}
            disabled={busy}
            onClick={() => { setBusy(true); void dropTaskScan(row, f.id).finally(() => setBusy(false)); }}
          >
            Remove
          </button>
        </span>
      ))}
      <button
        type="button"
        class="btn"
        data-testid={`task-file-add-${row.task_definition_id}`}
        aria-label={`Attach a file for ${row.definition_name}`}
        onClick={() => setAttaching(true)}
      >
        Attach
      </button>
      {attaching ? (
        <AttachBox
          title={row.definition_name}
          definitionId={row.task_definition_id}
          testPrefix={`row-${row.task_definition_id}-`}
          onClose={() => setAttaching(false)}
          onFile={(file) => addTaskScan(row, file)}
          onLink={(url, name) => addTaskLink(row, url, name)}
        />
      ) : null}
    </div>
  );
}

/** Which server route can open this row's report file for an assistant, if any.
 * Filed and Suspended-Accounts reports are not stored by row, so they keep the Drive link. */
export function reportProxy(row: TaskRow): { kind: TaskFileKind; id: number } | null {
  const href = row.attachment_url || row.report_url;
  if (!href) return null;
  if (row.report_url && row.report_url === href && row.id !== null) return { kind: 'report', id: row.id };
  if (row.filed_report || row.report_landed_at || row.report_slot) return { kind: 'filed', id: row.task_definition_id };
  if (row.attachment_url) return { kind: 'instructions', id: row.task_definition_id };
  return null;
}

/** This task's own Drive files, so Drive links typed into its instructions can open for an assistant. */
export function knownTaskFiles(row: TaskRow): KnownTaskFile[] {
  const out: KnownTaskFile[] = [];
  for (const f of row.files ?? []) {
    const driveId = f.url ? driveIdOf(f.url) : null;
    if (driveId) out.push({ kind: 'attachment', id: f.id, name: f.name, driveId });
  }
  const own = row.attachment_url && !row.filed_report && !row.report_landed_at ? driveIdOf(row.attachment_url) : null;
  if (own) out.push({ kind: 'instructions', id: row.task_definition_id, name: row.attachment_name, driveId: own });
  return out;
}

function ReportLink({ row }: { row: TaskRow }): JSX.Element {
  const href = row.attachment_url || row.report_url || null;
  if (!href) {
    if (reportExpected(row)) {
      return <MissingReportAdd row={row} testId={`task-report-add-${row.task_definition_id}`} />;
    }
    return <span class="muted">&mdash;</span>;
  }
  const name = row.attachment_name || 'the report';
  const landed = row.report_landed_at ? formatReportLanded(row.report_landed_at) : '';
  return (
    <span class="report-landed">
      <TaskFileLink
        class="drive-mark"
        kind={reportProxy(row)?.kind}
        id={reportProxy(row)?.id}
        href={href}
        name={name}
        testId={`task-attachment-${row.task_definition_id}`}
        ariaLabel={`Open ${name} for ${row.definition_name} in Google Drive`}
        title={landed ? `${name} · ${landed}` : name}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <span class="sr-only">{name}</span>
      </TaskFileLink>
      {landed ? (
        <small class="muted" data-testid={`task-report-landed-${row.task_definition_id}`}>{landed}</small>
      ) : null}
    </span>
  );
}

function NoteDrawer(
  { row, onClose }:
  { row: TaskRow; onClose: () => void },
): JSX.Element {
  const profile = useResource<TaskProfileData>(
    taskProfileKey(row.task_definition_id, row.period_start),
    () => fetchTaskProfile(row.task_definition_id, row.period_start),
  );
  const notes = profile.status === 'ready'
    ? (profile.data.notes ?? []).map((n) => ({
      id: n.id, body: n.body, author: n.author, at: n.created_at,
    }))
    : [];

  return (
    <NotePanel
      title={row.definition_name}
      notes={notes}
      loading={profile.status === 'loading'}
      onClose={onClose}
      onSave={async (text) => {
        await authedWrite('POST', `/api/v1/task-definitions/${row.task_definition_id}/notes`, { body: text });
        await refreshTaskNotes(row);
      }}
      onDelete={async (id) => {
        await authedWrite('DELETE', `/api/v1/task-notes/${id}`);
        await refreshTaskNotes(row);
      }}
    />
  );
}

async function refreshTaskNotes(row: TaskRow): Promise<void> {
  await refetchResource<TaskProfileData>(
    taskProfileKey(row.task_definition_id, row.period_start),
    () => fetchTaskProfile(row.task_definition_id, row.period_start),
  );
  const n = (peekResource<TaskProfileData>(taskProfileKey(row.task_definition_id, row.period_start))?.notes ?? []).length;
  const current = getStandingTable()[standingKey(row)] ?? row;
  await upsertEntity('standing', standingKey(current), {
    ...current,
    note_count: n,
    notes: n > 0 ? current.notes : '',
  });
}

function StandingNoteButton(
  { row, onOpen }:
  { row: TaskRow; onOpen: () => void },
): JSX.Element {
  const hasNote = (row.note_count ?? 0) > 0;
  return (
    <NoteMark
      hasNote={hasNote}
      label={row.definition_name}
      testId={`standing-note-${row.task_definition_id}`}
      onOpen={onOpen}
    />
  );
}

/** One row of the STANDING list: the task, when it is due, its status, and the
 * controls to act on it. */
function StandingLine(
  { row, now, listMode, draft, onDraft, onOpen, onRemove, onOpenNote, rowError, showCategory, showDuration }:
  {
    row: TaskRow; now: Date;
    listMode: ListMode;
    draft: RowDraft | null;
    onDraft: (next: RowDraft) => void;
    onOpen: (row: TaskRow) => void;
    onRemove: (row: TaskRow) => void;
    onOpenNote: (row: TaskRow) => void;
    rowError: string | null;
    /** DAILY has no categories at all — the column is absent, not empty. */
    showCategory: boolean;
    showDuration: boolean;
  },
): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const display = displayStatus(row, now);
  const detail = stateDetail(display, row);
  const editing = listMode === 'edit' && draft !== null;

  async function run(fn: () => Promise<unknown>) {
    setSaving(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr data-testid={`standing-row-${row.task_definition_id}`}>
      <td>
        {/* REMOVAL MODE'S ×, owner 2026-08-16: "when you click remove you get
            little x next to each line and you can delete one." It exists ONLY
            while removal mode is on. A delete control sitting permanently on
            every one of 55 rows is a mis-click waiting to happen, which is why
            he described it as a mode rather than a column. */}
        {listMode === 'remove' && (
          <button
            type="button"
            class="row-x"
            data-testid={`standing-remove-${row.task_definition_id}`}
            aria-label={`Remove ${row.definition_name} from the standing list`}
            onClick={() => onRemove(row)}
          >
            &times;
          </button>
        )}
        {editing ? (
          <input
            type="text"
            class="row-edit"
            aria-label={`Task name for ${row.definition_name}`}
            value={draft.name}
            onInput={(e: Event) => onDraft({ ...draft, name: (e.target as HTMLInputElement).value })}
          />
        ) : (
          // THE TASK NAME IS THE DOOR. Owner: "every task item should be
          // clickable which goes into its own task profile window." A <button>
          // rather than a styled <div> so it is reachable by keyboard and
          // announced as a control.
          <button
            type="button"
            class="link tk"
            data-testid={`standing-open-${row.task_definition_id}`}
            onClick={() => onOpen(row)}
          >
            {row.definition_name}
          </button>
        )}
        {/* The reason a task was sent back is the single most useful thing on
            this row for the person who has to redo it, so it is shown here and
            not only in the record below. */}
        {detail ? <small class={detail.cls}>{detail.text}</small> : null}
        {error ? <small class="bad" role="alert">{error}</small> : null}
        {/* A failure that belongs to THIS row of a whole-list save is printed on
            THIS row. "Something failed" over a table of 55 is not a report. */}
        {rowError ? <small class="bad" role="alert">{rowError}</small> : null}
      </td>
      {/* The category, as a chip — the same shape the old board drew
          (`tk_categoryChip_`). Read from the owner's own Tasks sheet, never
          inferred from the task's name. The whole column is absent on DAILY. */}
      {showCategory ? (
        <td>
          {row.category
            ? <span class="role-chip">{row.category}</span>
            : <span class="muted">&mdash;</span>}
        </td>
      ) : null}
      <td>
        {editing ? (
          <input
            type="text"
            class="row-edit"
            aria-label={`Due rule for ${row.definition_name}`}
            value={draft.dueRule}
            onInput={(e: Event) => onDraft({ ...draft, dueRule: (e.target as HTMLInputElement).value })}
          />
        ) : row.due_rule}
      </td>
      {showDuration ? (
        <td>
          {editing ? (
            <select
              class="row-edit"
              aria-label={`Duration for ${row.definition_name}`}
              value={draft.duration}
              onChange={(e: Event) => onDraft({ ...draft, duration: (e.target as HTMLSelectElement).value })}
            >
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          ) : (DURATION_LABEL[row.duration ?? ''] ?? row.duration ?? '—')}
        </td>
      ) : null}
      <td>
        <select
          value={row.status}
          disabled={saving}
          aria-label={`Status for ${row.definition_name}`}
          onChange={(e: Event) => {
            const next = (e.target as HTMLSelectElement).value;
            if (next === row.status) return;
            void run(() => setStatus(row, next as StoredStatus));
          }}
        >
          {/* A task can BE in a status this menu may not SET — SENT_BACK and
              VERIFIED each have their own endpoint with rules a generic setter
              must not route around. With no matching option the browser shows
              the first one instead, so a sent-back task read "Not started" and
              the control lied about the row it was attached to. It is offered
              as a disabled option: visible, accurate, unselectable. */}
          {!SETTABLE_STATUSES.includes(row.status) && (
            <option value={row.status} disabled>{STATUS_LABEL[row.status]}</option>
          )}
          {SETTABLE_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
        {display === 'OVERDUE' ? <small class="bad">Overdue</small> : null}
      </td>
      <td>
        <ReportLink row={row} />
      </td>
      <td>
        <FilesCell row={row} />
      </td>
      {/* THE INLINE SUBMIT. One click marks the task done and moves it to the
          completed table below. On a task nobody has touched this period there
          is no status row yet — `setStatus` posts to the definition instead and
          the server creates the row, the period and the event in one statement. */}
      <td class="submit-cell">
        <button
          type="button"
          class="send"
          disabled={saving}
          aria-label={`Submit ${row.definition_name} as done`}
          onClick={() => void run(() => setStatus(row, 'DONE'))}
        >
          Submit
        </button>
      </td>
      <td>
        <StandingNoteButton row={row} onOpen={() => onOpenNote(row)} />
      </td>
      {/* THE PER-ROW Edit AND Remove BUTTONS ARE GONE, and not kept "just in
          case". Owner, 2026-08-16: "these buttons Edit the whol task list not
          just one item so do this and remove the inline edit remove." Editing
          is now a mode over the whole card (one Edit, one Save, one Cancel) and
          removing is a mode that puts an × on each line. Two ways to edit the
          same list is how the two drift apart. */}
    </tr>
  );
}

/** ADD a standing task. Rendered for owner/director only.
 *
 * Add-only since 2026-08-16: renaming used to happen here too, and now happens
 * in the whole-list edit mode instead (owner: "these buttons Edit the whol task
 * list not just one item"). */
function StandingEditor(
  { cadence, clinic, onDone }:
  { cadence: string; clinic: string; onDone: () => void },
): JSX.Element {
  const [name, setName] = useState('');
  const [dueRule, setDueRule] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await addStandingTask(cadence, name.trim(), dueRule.trim(), clinic);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="standing-editor">
      <label>
        Task{' '}
        <input
          type="text"
          value={name}
          disabled={saving}
          aria-label="Task name"
          onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
        />
      </label>{' '}
      <label>
        Due{' '}
        <input
          type="text"
          value={dueRule}
          disabled={saving}
          aria-label="When it is due"
          onInput={(e: Event) => setDueRule((e.target as HTMLInputElement).value)}
        />
      </label>{' '}
      <button
        type="button"
        class="send"
        disabled={saving || !name.trim() || !dueRule.trim()}
        onClick={() => void submit()}
      >
        Add task
      </button>{' '}
      <button type="button" class="mini sm" disabled={saving} onClick={onDone}>Cancel</button>
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </div>
  );
}

// --- the completed table (bottom) -----------------------------------------

type PerformanceFilter = 'all' | 'done' | 'incomplete';

/** One period's record: what was done, by whom, when — and what was not done at
 * all. Read-only by construction: this table describes the past. */
function CompletedTable(
  { rows, now, heading, subtitle, children, allowUndo = true, onOpen }:
  {
    rows: TaskRow[];
    now: Date;
    heading: string;
    subtitle?: string;
    children?: JSX.Element | false | null;
    allowUndo?: boolean;
    onOpen?: (row: TaskRow) => void;
  },
): JSX.Element {
  const [performance, setPerformance] = useState<PerformanceFilter>('all');
  const [showNotesFiles, setShowNotesFiles] = useState(false);
  const undo = useWrite();

  // Notes, comments and files for this clinic and period: one request feeds
  // both the per-row count and the "Notes & files" list.
  const first = rows[0];
  const nfClinic = first ? first.location_code : null;
  const nfStart = first ? asDateString(first.period_start) : '';
  const nfEnd = first ? asDateString(first.period_end) : '';
  const notesFiles = useResource<{ items: NoteFileItem[] }>(
    nfClinic && nfStart && nfEnd ? `notes-files:${nfClinic}:${nfStart}:${nfEnd}` : null,
    () => authedGet<{ items: NoteFileItem[] }>(notesFilesUrl(nfClinic as string, nfStart, nfEnd)),
  );
  const nfItems = notesFiles.status === 'ready' ? (notesFiles.data.items ?? []) : [];
  const nfCounts = noteFileCounts(nfItems);

  const doneCount = rows.filter((r) => recordStatus(r, now) !== 'INCOMPLETE').length;
  const filtered = rows.filter((r) => {
    const record = recordStatus(r, now);
    if (performance === 'done') return record !== 'INCOMPLETE';
    if (performance === 'incomplete') return record === 'INCOMPLETE';
    return true;
  });

  // Sorting keys are the VALUES, not what the cell renders: Status sorts on
  // the recorded state rather than on the words in the chip, and When sorts on
  // the timestamp rather than on the formatted date.
  const sorted = useTableSort(filtered, {
    task: (r) => r.definition_name,
    status: (r) => recordStatus(r, now),
    who: (r) => r.completed_by,
    when: (r) => r.completed_at,
    report: (r) => r.report_url,
  });
  return (
    <div class="card">
      <div class="section-head">
        <h2>{heading}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {/* The day picker lives INSIDE this card, above its table — it belongs to
          this table and nothing else (owner, 2026-08-15), and a control floating
          between two cards reads as belonging to neither. */}
      {children}
      <div class="day-picker">
        <label>
          Performance{' '}
          <select
            data-testid="performance-picker"
            value={performance}
            onChange={(e: Event) => setPerformance((e.target as HTMLSelectElement).value as PerformanceFilter)}
          >
            <option value="all">All results</option>
            <option value="done">Completed only</option>
            <option value="incomplete">Incomplete only</option>
          </select>
        </label>
        <span data-testid="performance-summary">{doneCount} of {rows.length} done</span>
        {nfClinic ? (
          <button
            type="button" class="mini" data-testid="notes-files-toggle"
            onClick={() => setShowNotesFiles(!showNotesFiles)}
          >
            Notes &amp; files ({nfItems.length})
          </button>
        ) : null}
      </div>
      {showNotesFiles ? <NotesFilesList items={nfItems} /> : null}
      {allowUndo && undo.error ? <small class="bad" role="alert">{undo.error}</small> : null}
      {filtered.length === 0 ? (
        <EmptyState label={rows.length === 0 ? 'Nothing recorded for this period yet.' : 'No matching results.'} />
      ) : (
        <div class="table-wrap">
          <table class="task-table">
            <thead>
              <tr>
                <SortableTh label="Task" sortKey="task" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Status" sortKey="status" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Who" sortKey="who" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="When" sortKey="when" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Report" sortKey="report" state={sorted.state} onSort={sorted.toggle} />
                <th>Verified</th>
                <th>Notes</th>
                <th>Notes &amp; files</th>
                {allowUndo ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((row) => {
                const record = recordStatus(row, now);
                const detail = stateDetail(record, row);
                const at = row.completed_at ? new Date(row.completed_at) : null;
                const stamped = at && !Number.isNaN(at.getTime());
                return (
                  <tr key={`${row.task_definition_id}:${row.location_code}`}>
                    <td>
                      {onOpen ? (
                        <button
                          type="button"
                          class="link tk"
                          data-testid={`completed-open-${row.task_definition_id}`}
                          onClick={() => onOpen(row)}
                        >
                          {row.definition_name}
                        </button>
                      ) : (
                        <div class="tk">{row.definition_name}</div>
                      )}
                      {detail && record === 'INCOMPLETE'
                        ? <small class={detail.cls}>{detail.text}</small>
                        : null}
                    </td>
                    <td class={record === 'INCOMPLETE' ? 'bad' : ''}>{STATUS_LABEL[record]}</td>
                    <td>{row.completed_by ?? <span class="muted">&mdash;</span>}</td>
                    {/* Date AND time (R-15) — "that's how we will also track who
                        isn't doing their job". A row that was never completed has
                        no timestamp, and an em dash is the honest answer. */}
                    <td>
                      {stamped
                        ? `${formatDate(clinicNow(at as Date))} at ${formatClock(at as Date)}`
                        : <span class="muted">&mdash;</span>}
                    </td>
                    <td>
                      {row.report_url
                        ? <a class="doc-link" href={row.report_url} target="_blank" rel="noopener noreferrer">Report</a>
                        : <span class="muted">&mdash;</span>}
                    </td>
                    <td data-testid={`completed-verified-${row.task_definition_id}`}>
                      {row.verified_by
                        ? `${row.verified_by}${stampOf(row.verified_at) ? ` · ${stampOf(row.verified_at)}` : ''}`
                        : <span class="muted">&mdash;</span>}
                    </td>
                    <td>{row.notes ? row.notes : <span class="muted">&mdash;</span>}</td>
                    <td data-testid={`completed-nf-${row.task_definition_id}`}>
                      {nfCounts.get(Number(row.task_definition_id))
                        ? (
                          <button type="button" class="link" onClick={() => (onOpen ? onOpen(row) : setShowNotesFiles(true))}>
                            {nfCounts.get(Number(row.task_definition_id))}
                          </button>
                        )
                        : <span class="muted">&mdash;</span>}
                    </td>
                    {allowUndo ? (
                      <td>
                        {canUndoCompleted(row) ? (
                          <button
                            type="button"
                            class="mini"
                            data-testid={`task-undo-${row.id}`}
                            disabled={undo.busy}
                            aria-label={`Undo ${row.definition_name}`}
                            onClick={() => void undo.run(() => reopenTask(row))}
                          >
                            Undo
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One NAMED period's results table (weekly / monthly), fetched on demand.
 *
 * Owner, 2026-08-17: "dont see month picker on bottom task results table or
 * weekly date range picker". Daily had its calendar input; weekly and monthly
 * had no way to look at any period but the current one. This is the same
 * mechanism as PastDay, driven by a period LABEL the picker chose rather than
 * by a date — labels are the join key `GET /api/v1/tasks?period=` reads. */
function PeriodResults(
  { label, clinic, cadence, now, picker, onOpen }:
  { label: string; clinic: string; cadence: Cadence; now: Date; picker: JSX.Element; onOpen?: (row: TaskRow) => void },
): JSX.Element {
  const tasks = useResource<{ tasks: TaskRow[] }>(
    `tasks:${label}`,
    () => authedGet(`/api/v1/tasks?period=${encodeURIComponent(label)}`),
  );

  if (tasks.status === 'loading') return <EmptyState label="Loading that period…" />;
  if (tasks.status === 'fault') {
    return <FaultState name="could not load that period" detail={tasks.error} />;
  }

  const rows = forClinic(tasks.data.tasks || [], clinic)
    .filter((r) => r.cadence === cadence)
    .filter((r) => isRecorded(r, now))
    .sort((a, b) => a.definition_name.localeCompare(b.definition_name));

  return (
    <CompletedTable rows={rows} now={now} heading="TASK RESULTS" subtitle={label} allowUndo={false} onOpen={onOpen}>
      {picker}
    </CompletedTable>
  );
}

/** One past day's completed table, fetched on demand. Mounted only while a past
 * date is chosen, so a session that never opens the calendar makes no extra
 * request. `useResource` never re-fetches a key that resolved, so opening the
 * same day twice costs one request. */
function PastDay(
  { day, clinic, now, picker, onOpen }:
  { day: string; clinic: string; now: Date; picker: JSX.Element; onOpen?: (row: TaskRow) => void },
): JSX.Element {
  const periods = useResource<{ periods: TaskPeriodSummary[] }>(
    'task-periods',
    () => authedGet('/api/v1/task-periods'),
  );

  const label = periods.status === 'ready'
    ? dailyLabelFor(periods.data.periods || [], day)
    : null;

  const tasks = useResource<{ tasks: TaskRow[] }>(
    label ? `tasks:${label}` : null,
    () => authedGet(`/api/v1/tasks?period=${encodeURIComponent(label as string)}`),
  );

  if (periods.status === 'loading') return <EmptyState label="Loading that day…" />;
  if (periods.status === 'fault') {
    return <FaultState name="could not load the list of days" detail={periods.error} />;
  }
  if (!label) {
    return (
      <div class="card">
        <div class="section-head"><h2>Completed &mdash; {formatDay(day)}</h2></div>
        {picker}
        <EmptyState label={`Nothing was recorded on ${formatDay(day)}.`} />
      </div>
    );
  }
  if (tasks.status === 'loading') return <EmptyState label="Loading that day…" />;
  if (tasks.status === 'fault') {
    return <FaultState name="could not load that day's tasks" detail={tasks.error} />;
  }

  const rows = forClinic(tasks.data.tasks || [], clinic)
    .filter((r) => r.cadence === 'DAILY')
    .sort((a, b) => a.definition_name.localeCompare(b.definition_name));

  return (
    <CompletedTable
      rows={rows}
      now={now}
      heading={`Completed — ${formatDay(day)}`}
      subtitle="A day that has already happened — read only."
      allowUndo={false}
      onOpen={onOpen}
    >
      {picker}
    </CompletedTable>
  );
}

// --- the page --------------------------------------------------------------

// Daily, Weekly, Monthly, then Manager Tasks (calendar comes later).
const CADENCE_ORDER = ['DAILY', 'WEEKLY', 'MONTHLY', 'MANAGER'] as const;
type Cadence = (typeof CADENCE_ORDER)[number];
const STATS_TAB_ID = 'STATS';
const ALERTS_TAB_ID = 'ALERTS';
/** Owner, 2026-10-06 ("yes to all" on the view-differences audit): the
 * Stats tab is still an empty shelf, so it is HIDDEN for everyone until he
 * names its tables. The code path stays; flip this to bring it back. */
const SHOW_TASKS_STATS_TAB: boolean = true;

const CADENCE_LABEL: Record<Cadence, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  MANAGER: 'Manager',
};
const DURATION_LABEL: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

/** The top card's heading, per cadence (owner, 2026-08-17). */
const STANDING_HEADING: Record<Cadence, string> = {
  DAILY: 'DAILY TASKS',
  WEEKLY: 'WEEKLY TASKS',
  MONTHLY: 'MONTHLY TASKS',
  MANAGER: 'MANAGER TASKS',
};

/** The prefix `db/import_task_definitions.js` and src/task_periods.js both
 * write into `period_label` — "Day 2026-08-17 (Aug 17)", "Week 2026-W34
 * (Aug 17-23)", "Month 2026-08 (Aug 2026)". Matching on it is how a flat list
 * of labels is split back into cadences; the labels are a fixed vocabulary
 * written in one place, not free text this page is guessing at. */
const PERIOD_PREFIX: Record<Cadence, string> = {
  DAILY: 'Day ',
  WEEKLY: 'Week ',
  MONTHLY: 'Month ',
  MANAGER: 'Week ',
};

/** Every period the API knows for one cadence, newest first — what the week
 * and month pickers list. */
export function periodsForCadence(
  periods: TaskPeriodSummary[],
  cadence: Cadence,
): TaskPeriodSummary[] {
  return periods
    .filter((p) => p.period_label.startsWith(PERIOD_PREFIX[cadence]))
    .slice()
    .sort((a, b) => (asDateString(b.period_start) < asDateString(a.period_start) ? -1 : 1));
}

export function TasksPage(): JSX.Element {
  const [clinic, setClinic] = useState<string | null>(null);
  const [management, setManagement] = useState(false);
  // Stats moved here from People (owner, 2026-09-26: "move Stats from people
  // to Tasks menu"); the tab sits after Management, owner/director only like
  // Management (a lone-clinic manager keeps a tab-less page), still an empty
  // shelf until he names its tables.
  const [stats, setStats] = useState(false);
  const showManagementTab = SHOW_EMPTY_MANAGEMENT_TABS && canSeeManagementTab();
  // Stats and Alerts are for every role (owner, 2026-11-02); the server scopes.
  // An assistant has no Stats / Alerts (the server refuses them).
  const showStatsTab = SHOW_TASKS_STATS_TAB && session.value.me?.role !== 'ASSISTANT';
  const [alertsTab, setAlertsTab] = useState(false);
  const alertsBadge = useAlertsBadge();
  const [cadence, setCadence] = useState<Cadence | null>(null);
  // null = the current period, i.e. the store's own rows and no network call.
  const [day, setDay] = useState<string | null>(null);
  // null = the CURRENT weekly/monthly period (the store's own rows, no network).
  // A label = look at that period instead (owner, 2026-08-17).
  const [periodChoice, setPeriodChoice] = useState<string | null>(null);
  // The list the week/month picker offers. READ FROM THE CACHE, never fetched
  // here: bootstrap already loads exactly this list at sign-in and primes the
  // key, and INV-1 budgets NAVIGATION at zero network calls. If the bootstrap
  // slice faulted there is simply nothing to pick from, which the picker shows
  // honestly rather than papering over with a second request.
  const knownPeriods = peekResource<{ periods: TaskPeriodSummary[] }>('task-periods');
  const [periodListTick, setPeriodListTick] = useState(0);
  void periodListTick;
  // Which task's own page is open, by DEFINITION id — the profile belongs to
  // the definition, not to one clinic's status row (which may not exist yet).
  // `'new'` is Add: a blank profile so every field is filled before it exists.
  const [openTask, setOpenTask] = useState<number | 'new' | null>(null);
  const [openTaskRow, setOpenTaskRow] = useState<TaskRow | null>(null);

  function openCompleted(row: TaskRow): void {
    setOpenTaskRow(row);
    setOpenTask(row.task_definition_id);
  }
  const [listMode, setListMode] = useState<ListMode>('view');
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [listBusy, setListBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [noteKey, setNoteKey] = useState<string | null>(null);

  let standingRows: TaskRow[];
  let periodRows: TaskRow[];
  try {
    standingRows = Object.values(getStandingTable());
    periodRows = Object.values(getTaskTable());
  } catch (err) {
    // INV-9: a visible, named fault — never a blank page.
    return (
      <div class="main">
        <div class="page-header"><h1>Tasks</h1></div>
        <FaultState name="task data unreadable" detail={err instanceof Error ? err.message : String(err)} />
      </div>
    );
  }

  const clinics = visibleClinics();
  const activeClinic = activeClinicFrom(clinic, clinics);
  const myStandingEarly = forClinic(standingRows, activeClinic);
  const draftCadence = (cadence && CADENCE_ORDER.includes(cadence) ? cadence : null)
    ?? CADENCE_ORDER.find((c) => myStandingEarly.some((r) => r.cadence === c))
    ?? 'DAILY';

  // THE TASK PROFILE. Rendered instead of the page, exactly the way People
  // renders a person instead of the roster — same idiom, same "Back to" control,
  // no router route (Tasks has never had one).
  if (openTask === 'new') {
    return (
      <TaskProfile
        creating
        row={blankTask(draftCadence, activeClinic)}
        siblings={[]}
        onBack={() => setOpenTask(null)}
        onCreated={(id) => setOpenTask(id)}
      />
    );
  }
  if (openTask !== null) {
    const mine = [...standingRows, ...periodRows].filter((r) => r.task_definition_id === openTask);
    const here = mine.find((r) => r.location_code === activeClinic)
      ?? mine[0]
      ?? (openTaskRow && openTaskRow.task_definition_id === openTask ? openTaskRow : null);
    if (here) {
      return (
        <TaskProfile
          row={here}
          siblings={mine.length ? mine : [here]}
          onBack={() => { setOpenTask(null); setOpenTaskRow(null); }}
        />
      );
    }
    // The task went away underneath us (retired while the profile was open).
    // Falling through to the list beats a blank page (INV-9). Deliberately NOT
    // a setState here — setting state during render is how Preact gets an
    // infinite render loop, and the stale id is harmless once no row matches it.
  }

  // The cadence tabs come from the STANDING list, not from whatever periods
  // happen to have rows. The standing list is the same every day, so the tabs
  // are too — a cadence does not disappear because nobody has done one of its
  // tasks yet.
  const myStanding = forClinic(standingRows, activeClinic);
  const clinicLists = CADENCE_ORDER.filter((c) => c !== 'MANAGER' && myStanding.some((r) => r.cadence === c));
  const cadencesPresent: Cadence[] = [...clinicLists, 'MANAGER'];
  const activeCadence = cadence && cadencesPresent.includes(cadence)
    ? cadence
    : cadencesPresent[0] ?? null;
  const canEditList = canEditStandingList(activeCadence);

  const now = new Date();
  const today = todayString(now);

  const forCadence = myStanding
    .filter((r) => r.cadence === activeCadence)
    .sort((a, b) => a.definition_name.localeCompare(b.definition_name));

  // The top table holds what is still OPEN. Submitting moves a row down, which
  // is the owner's own description of what Submit does.
  const openRows = forCadence.filter((r) => !TERMINAL_STATUSES.includes(r.status));

  // Click-to-sort (owner, 2026-08-19). The default is untouched — no column
  // chosen means the alphabetical order above survives, so the page a manager
  // knows does not rearrange itself on load.
  const standingSort = useTableSort(openRows, {
    task: (r) => r.definition_name,
    category: (r) => r.category ?? null,
    due: (r) => r.due_rule,
    duration: (r) => r.duration ?? null,
    status: (r) => r.status,
    report: (r) => r.report_url,
  });
  const open = standingSort.rows;
  const noteRow = noteKey
    ? standingRows.find((r) => standingKey(r) === noteKey) ?? null
    : null;

  // The bottom table, for the CURRENT period: only what has actually been
  // RECORDED.
  //
  // A `task_status` row exists for every definition from the moment a period is
  // seeded, all of them NOT_STARTED, so listing the store's rows unfiltered
  // reprinted the entire standing list underneath itself — the same thirteen
  // tasks twice, the lower copy saying "Not started — — —" on every line. Caught
  // by clicking the real page, not by any test.
  //
  // A record is a row something HAPPENED to (a terminal status) or one whose
  // period ended with it still open (INCOMPLETE). A not-started task in a period
  // that is still running is not a record of anything yet; it is the top table's
  // job to show it.
  // WEEKLY and MONTHLY carry categories; DAILY does not — owner, 2026-08-17,
  // and his own source sheet agrees (all 13 daily rows have a blank `type`).
  const showCategory = activeCadence !== 'DAILY' && activeCadence !== 'MANAGER';
  const showDuration = activeCadence === 'MANAGER';

  const currentRecord = forClinic(periodRows, activeClinic)
    .filter((r) => r.cadence === activeCadence)
    .filter((r) => isRecorded(r, now))
    .sort((a, b) => a.definition_name.localeCompare(b.definition_name));

  const periodLine = forCadence.length > 0 && forCadence[0].period_label
    ? renderPeriodLabel(forCadence[0])
    : null;

  /**
   * Save the whole-list edit.
   *
   * Only the rows that CHANGED are sent, one write each, each with its own
   * Idempotency-Key (authedWrite mints one per call). Sequential rather than
   * parallel: 55 simultaneous writes through one Durable Object gains nothing —
   * it serialises them anyway — and would make the per-row error reporting below
   * arrive in an order unrelated to the table.
   *
   * A row that fails does NOT roll the others back and is NOT reported as done.
   * Its own message lands on its own line, the successful rows stay saved, and
   * the card stays in edit mode so the failure is still in front of the person
   * who caused it.
   */
  async function saveWholeList(): Promise<void> {
    const changes = changedDrafts(drafts, open);
    if (changes.length === 0) { setListMode('view'); setDrafts({}); return; }

    setListBusy(true);
    setListError(null);
    const failures: Record<number, string> = {};
    for (const change of changes) {
      const siblings = standingRows.filter((r) => r.task_definition_id === change.id);
      try {
        // eslint-disable-next-line no-await-in-loop
        await editStandingTask(change.id, change.name, change.dueRule, siblings, change.duration || null);
      } catch (err) {
        failures[change.id] = err instanceof ApiError ? err.message : String(err);
      }
    }
    setListBusy(false);
    setRowErrors(failures);
    const failed = Object.keys(failures).length;
    if (failed === 0) {
      setDrafts({});
      setListMode('view');
      return;
    }
    // Named plainly, with the count. "Saved" over a partial failure is the one
    // outcome that must never happen here.
    setListError(
      `${changes.length - failed} of ${changes.length} saved. `
      + `${failed} could not be saved — see the message on those rows.`,
    );
  }

  /** Retire one task from removal mode's ×. A retire, never a delete: every
   * historic completed row points at this definition (migration 027). */
  async function removeOne(row: TaskRow): Promise<void> {
    // eslint-disable-next-line no-alert
    const sure = globalThis.confirm(
      `Remove "${row.definition_name}" from the standing list?\n\n`
      + 'It stops appearing from now on. Everything already recorded against '
      + 'it stays in the completed table.',
    );
    if (!sure) return;
    setListBusy(true);
    setListError(null);
    try {
      await retireStandingTask(
        row.task_definition_id,
        standingRows.filter((r) => r.task_definition_id === row.task_definition_id),
      );
    } catch (err) {
      setRowErrors({
        ...rowErrors,
        [row.task_definition_id]: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setListBusy(false);
    }
  }

  return (
    <div class="main">
      {/* The page header is the TITLE and nothing else (owner, 2026-08-15:
          "the pages need to look the same without text that doesn't belong,
          which shifts the page sections around"). A descriptive line under the
          h1 pushed every card down by a different amount on every page, so no
          two pages started at the same height. What the page is for belongs in
          the section heads, where it sits beside the thing it describes. */}
      <div class="page-header">
        <h1>Tasks</h1>
      </div>

      <ClinicTabs
        clinics={clinics}
        active={showManagementTab && management ? MANAGEMENT_TAB_ID : showStatsTab && alertsTab ? ALERTS_TAB_ID : showStatsTab && stats ? STATS_TAB_ID : activeClinic}
        onSelect={(code) => { setClinic(code); setManagement(false); setStats(false); setAlertsTab(false); }}
        // Management is listed here by hand so Stats can come last, the
        // same order People kept; ClinicTabs would otherwise append it.
        // Both are empty and hidden for everyone until they have content
        // (owner, 2026-10-06 — SHOW_EMPTY_MANAGEMENT_TABS / SHOW_TASKS_STATS_TAB).
        after={[
          ...(showManagementTab ? [{ id: MANAGEMENT_TAB_ID, label: 'Management' }] : []),
          ...(showStatsTab ? [{ id: STATS_TAB_ID, label: 'Stats' }, { id: ALERTS_TAB_ID, label: alertsBadge > 0 ? `Alerts (${alertsBadge})` : 'Alerts' }] : []),
        ]}
        management={false}
        onSelectAfter={(id) => {
          setStats(id === STATS_TAB_ID);
          setAlertsTab(id === ALERTS_TAB_ID);
          setManagement(id === MANAGEMENT_TAB_ID);
        }}
      />

      {showManagementTab && management ? (
        <ManagementPlaceholder section="Tasks" />
      ) : showStatsTab && alertsTab ? (
        <TaskAlertsView />
      ) : showStatsTab && stats ? (
        <TaskStatsView />
      ) : (
        <>
      {cadencesPresent.length > 0 && (
        <div class="cadence-tabs" role="tablist" aria-label="Cadence">
          {cadencesPresent.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={c === activeCadence}
              class={c === activeCadence ? 'tab active' : 'tab'}
              // Switching cadence leaves every mode: the drafts belong to the
              // rows that were on screen, and carrying them onto a different
              // list would edit tasks nobody was looking at.
              onClick={() => {
                setCadence(c);
                setListMode('view');
                setDrafts({});
                setRowErrors({});
                setListError(null);
              }}
            >
              {CADENCE_LABEL[c]}
            </button>
          ))}
        </div>
      )}

      {/* THE STANDING LIST, as a wk33 card — owner, 2026-08-15: "those two
          sections should be tables like in the week 33 display. same background
          things really need to look the same". The two tables were sitting bare
          on the page background with a small grey label over each; the review
          document he reads every week puts every section in a white card with a
          15px heading and a one-line explanation. Same idiom here, same classes,
          so there is one card style in this app rather than a second one that
          drifts. D-37. */}
      <div class="card">
        {/* ADD, EDIT and REMOVE — ONE set, at the right end of the heading's
            own line (owner, 2026-08-16, both for "acting on the WHOLE list"
            and, separately, for "same line as table heading"). They used to
            live INSIDE the `<h2>` text itself; `.section-head-row` /
            `.section-head-actions` — the one shared rule, not a page-local
            flex declaration — is what now puts the heading left and the
            controls right on their own sibling line. OWNER and DIRECTOR
            only when canEdit — a manager sees them for THEIR clinic's board
            (L-29). The server refuses a write to another clinic (INV-3). */}
        <div class="section-head">
          <div class="section-head-row">
            {/* Owner, 2026-08-17: "where is says Standing List should say
                DAILY TASKS, WEEKLY TASKS, MONTHLY TASKS based on what was
                selected". The tab above already says which cadence you are in;
                the heading now says it too, so a screenshot of this card is
                self-describing. */}
            <h2>{STANDING_HEADING[activeCadence]}</h2>
            <div class="section-head-actions">
              {canEditList && listMode === 'view' && (
                <>
                  <button
                    type="button"
                    class="mini sm"
                    data-testid="standing-add"
                    aria-label="Add a task to this standing list"
                    onClick={() => setOpenTask('new')}
                  >
                    Add task
                  </button>
                  <button
                    type="button"
                    class="mini sm"
                    data-testid="standing-edit"
                    aria-label="Edit this whole standing list"
                    onClick={() => { setRowErrors({}); setListError(null); setDrafts(draftsFor(open)); setListMode('edit'); }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    class="mini sm"
                    data-testid="standing-remove-mode"
                    aria-label="Remove tasks from this standing list"
                    onClick={() => { setRowErrors({}); setListError(null); setListMode('remove'); }}
                  >
                    Remove
                  </button>
                </>
              )}
              {canEditList && listMode === 'edit' && (
                <>
                  <button
                    type="button"
                    class="mini sm"
                    data-testid="standing-cancel"
                    disabled={listBusy}
                    // Cancel reverts EVERY row at once — the drafts are simply
                    // thrown away, so nothing partially typed survives it.
                    onClick={() => { setDrafts({}); setRowErrors({}); setListError(null); setListMode('view'); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="send"
                    data-testid="standing-save"
                    disabled={listBusy}
                    onClick={() => void saveWholeList()}
                  >
                    {listBusy ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
              {canEditList && listMode === 'remove' && (
                <button
                  type="button"
                  class="mini sm"
                  data-testid="standing-remove-done"
                  /* Deliberately NOT disabled while a removal is in flight.
                     Leaving removal mode is not a write — it just puts the ×
                     away — and disabling it meant that clicking Done right
                     after an × was silently ignored, leaving every row still
                     showing its ×. Caught by task-profile.test.tsx. */
                  onClick={() => { setRowErrors({}); setListError(null); setListMode('view'); }}
                >
                  Done
                </button>
              )}
            </div>
          </div>
          {listMode === 'remove' && (
            <p>Click the × beside a task to take it off this list. Everything already recorded against it stays.</p>
          )}
          {listError ? <small class="bad" role="alert">{listError}</small> : null}
        </div>

        {standingRows.length === 0 ? (
          <EmptyState label={snapshot.value.loading ? 'Loading tasks…' : 'No standing tasks loaded yet.'} />
        ) : forCadence.length === 0 ? (
          <EmptyState label={activeCadence === 'MANAGER' ? 'No manager tasks yet.' : 'No tasks on this list.'} />
        ) : open.length === 0 ? (
          <EmptyState label="Everything on this list is done for this period." />
        ) : (
          <div class="table-wrap">
            <table class="task-table">
              <thead>
                <tr>
                  <SortableTh label="Task" sortKey="task" state={standingSort.state} onSort={standingSort.toggle} />
                  {/* DAILY has no categories — not "none yet", none at all: the
                      owner's own source sheet leaves the column blank for all
                      thirteen, and he asked for it gone here (2026-08-17). */}
                  {showCategory
                    ? <SortableTh label="Category" sortKey="category" state={standingSort.state} onSort={standingSort.toggle} />
                    : null}
                  <SortableTh label="Due" sortKey="due" state={standingSort.state} onSort={standingSort.toggle} />
                  {showDuration
                    ? <SortableTh label="Duration" sortKey="duration" state={standingSort.state} onSort={standingSort.toggle} />
                    : null}
                  <SortableTh label="Status" sortKey="status" state={standingSort.state} onSort={standingSort.toggle} />
                  <SortableTh label="Report" sortKey="report" state={standingSort.state} onSort={standingSort.toggle} />
                  <th>Files</th>
                  <th>Submit</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {open.map((row) => (
                  <StandingLine
                    key={standingKey(row)}
                    row={row}
                    now={now}
                    listMode={listMode}
                    draft={drafts[row.task_definition_id] ?? null}
                    onDraft={(next) => setDrafts({ ...drafts, [row.task_definition_id]: next })}
                    onOpen={(r) => setOpenTask(r.task_definition_id)}
                    onRemove={(r) => void removeOne(r)}
                    onOpenNote={(r) => setNoteKey(standingKey(r))}
                    rowError={rowErrors[row.task_definition_id] ?? null}
                    showCategory={showCategory}
                    showDuration={showDuration}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* The picker belongs to the BOTTOM table and nothing else (owner,
          2026-08-15: "really the day picker should be for the bottom table"), so
          it is rendered INSIDE that card rather than floating between the two.
          DAILY keeps its calendar input; WEEKLY and MONTHLY get a list of their
          own periods, newest first (owner, 2026-08-17) — a date box would ask
          someone to know which Monday a payroll week starts on, and the label
          already says the range in words. */}
      {(() => {
        const known = knownPeriods?.periods ?? [];
        const mine = periodsForCadence(known, activeCadence);
        const currentLabel = periodLine !== null && forCadence.length > 0
          ? forCadence[0].period_label
          : (mine[0]?.period_label ?? null);

        const dayPicker = (
          <div class="day-picker">
            <label>
              Completed on{' '}
              <input
                type="date"
                value={day ?? today}
                max={today}
                onInput={(e: Event) => {
                  const picked = (e.target as HTMLInputElement).value;
                  setDay(!picked || picked === today ? null : picked);
                }}
              />
            </label>
            {day && (
              <button type="button" class="mini sm" onClick={() => setDay(null)}>
                Back to today
              </button>
            )}
          </div>
        );

        const periodPicker = (
          <div class="day-picker">
            <label>
              {activeCadence === 'MONTHLY' ? 'Month' : 'Week'}{' '}
              <select
                data-testid="period-picker"
                value={periodChoice ?? currentLabel ?? ''}
                onFocus={() => {
                  if (peekResource('task-periods-full')) return;
                  void authedGet<{ periods: TaskPeriodSummary[] }>('/api/v1/task-periods')
                    .then(async (data) => {
                      await persistResource('task-periods', data);
                      primeResource('task-periods-full', true);
                      setPeriodListTick((n) => n + 1);
                    })
                    .catch(() => undefined);
                }}
                onChange={(e: Event) => {
                  const picked = (e.target as HTMLSelectElement).value;
                  setPeriodChoice(!picked || picked === currentLabel ? null : picked);
                }}
              >
                {mine.map((p) => (
                  <option key={p.period_label} value={p.period_label}>
                    {renderPeriodLabel({
                      period_label: p.period_label,
                      period_start: p.period_start,
                      period_end: p.period_end,
                    } as TaskRow)}
                  </option>
                ))}
              </select>
            </label>
            {periodChoice && (
              <button type="button" class="mini sm" onClick={() => setPeriodChoice(null)}>
                Back to current
              </button>
            )}
          </div>
        );

        const picker = activeCadence === 'DAILY' ? dayPicker : periodPicker;

        if (activeCadence === 'DAILY') {
          return day
            ? <PastDay day={day} clinic={activeClinic} now={now} picker={dayPicker} onOpen={openCompleted} />
            : (
              <CompletedTable
                rows={currentRecord}
                now={now}
                heading="TASK RESULTS"
                subtitle={periodLine ?? undefined}
                onOpen={openCompleted}
              >
                {dayPicker}
              </CompletedTable>
            );
        }

        return periodChoice
          ? (
            <PeriodResults
              label={periodChoice}
              clinic={activeClinic}
              cadence={activeCadence}
              now={now}
              picker={periodPicker}
              onOpen={openCompleted}
            />
          )
          : (
            <CompletedTable
              rows={currentRecord}
              now={now}
              heading="TASK RESULTS"
              subtitle={periodLine ?? undefined}
              onOpen={openCompleted}
            >
              {picker}
            </CompletedTable>
          );
      })()}
        </>
      )}
      <AssistantsPanel />
      {noteRow ? <NoteDrawer row={noteRow} onClose={() => setNoteKey(null)} /> : null}
    </div>
  );
}
